/**
 * The rating loop — the screen the whole product exists to put in front of a
 * table that has just finished a game.
 *
 * Four things here are load-bearing and every change should be checked against
 * them:
 *
 *  1. THE REVEAL IS THE SERVER'S. `Progress.revealed` arrives in the read model
 *     and is rendered as-is. This screen counts nothing, infers nothing and has
 *     no code path that turns "every answer I can see is in" into a reveal.
 *     `everyoneIn` without `revealed` is a real state — an offline shared device
 *     holding unsynced writes lands there — and it gets a sentence saying so.
 *  2. THE WRITE HAPPENS BEFORE THE SCREEN MOVES. Every change to a rating is
 *     committed the moment it is made, and a write that failed is kept and named
 *     until it lands. Nothing advances, hands off or clears a draft on the
 *     strength of a value that only exists in this component's state.
 *  3. WHOSE SCREEN THIS IS, IS A SERVER QUESTION TOO. The acting participant is
 *     sent as `?as=` and the server redacts the read model to them. When the
 *     phone changes hands the actor changes, the state is re-read, and the
 *     previous rater's numbers leave the device. Hiding them client-side would
 *     mean shipping them to the person they are hidden from.
 *  4. A SHARED DEVICE KNOWS ITS OWN WRITES. On the outbox path the ratings of
 *     the whole table are in this device's IndexedDB, so an offline round still
 *     progresses instead of asking Tom for a number Tom already gave. Those
 *     local rows are folded in under the server's own redaction rule — the value
 *     shows only to the person who entered it — and they never produce a reveal.
 *
 * Two capture modes share one control. The only differences are where the write
 * goes — decided inside `commitRating`, not here — and how the waiting state is
 * phrased, which `waitingLabel` already knows.
 */

import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";

import { DeleteNight } from "../../components/DeleteNight.tsx";
import { Stars } from "../../components/Stars.tsx";
import {
  STAR_COUNT,
  formatStars,
  fullValue,
  starFraction,
  starLabel,
} from "../../../shared/stars.ts";
import { BoxArt } from "../catalog/components.tsx";
import { navigate } from "../../main.tsx";
import type { ScreenProps } from "../../main.tsx";
import { conflicts, deadLetters, readLocal } from "../../store/outbox.ts";
import type { SidelinedOp } from "../../store/outbox.ts";
import { getSyncState, subscribeRecovery, subscribeSync } from "../../store/sync.ts";
import type { SyncState } from "../../store/sync.ts";
import { channelNotice, useSessionLive } from "./useSession.ts";
import {
  ApiError,
  aspectTarget,
  commitRating,
  grantVerdict,
  offlineLine,
  ratingRowId,
  readGrant,
  RefusedRating,
  skipRowNeeded,
  subQuestionTarget,
  takeBackSeat,
  writePathFor,
} from "./api.ts";
import type { ParticipantView, RatingTarget, Role } from "./api.ts";
import {
  OVERALL_STOPS,
  allowsNa,
  answeredStep,
  aspectProfile,
  buildSteps,
  cellsOf,
  deliveryStalled,
  followUpsByAspect,
  missingHeadline,
  nextRater,
  overallValueOf,
  ownCell,
  progressOfStep,
  ratingCells,
  stepShiftOnOpen,
  stopsOf,
  topicCellsOf,
  topicValueOf,
  visibleRatingCells,
  waitingLabel,
} from "./state.ts";
import type {
  Cell,
  OverallValue,
  Progress,
  RatingCell,
  RatingView,
  Step,
  SubQuestionView,
  TopicValue,
} from "./state.ts";
import type {
  CaptureMode,
  Entity,
  SessionParticipant,
  SessionStatus,
  Stop,
} from "../../../shared/types.ts";

// ---------------------------------------------------------------------------
// Which seat this device is sitting in
// ---------------------------------------------------------------------------

const SEAT_KEY = "pb-seat";

/**
 * Asked before a rating this phone is still holding leaves with the screen.
 *
 * Says what is at stake in one sentence — this device cannot keep it — because
 * the person reading it is standing at a table and gets one chance to answer.
 */
const LEAVE_UNSENT =
  "A rating on this screen has not reached the table yet, and this phone cannot hold it. Leave now and it is gone. Leave anyway?";

/**
 * The owner's own seat, remembered per session — but only on a QR night.
 *
 * On a shared device the seat is not a property of the device, it is whoever is
 * holding it right now, and remembering it across a reload would hand the next
 * person the last person's screen. So this is written on `MULTI_DEVICE` only,
 * and to `sessionStorage` for the same reason the join grant lives there: a
 * phone must not still be somebody's seat next Tuesday.
 */
function readSeat(sessionId: string): string | null {
  try {
    const raw = window.sessionStorage.getItem(`${SEAT_KEY}:${sessionId}`);
    return raw && raw.length > 0 ? raw : null;
  } catch {
    return null;
  }
}

function saveSeat(sessionId: string, participantId: string): void {
  try {
    window.sessionStorage.setItem(`${SEAT_KEY}:${sessionId}`, participantId);
  } catch {
    // Costs a re-pick after a reload; never worth failing a rating over.
  }
}

/** A roster seat as the read model sends it, holder kind included. */
export type Seat = Pick<SessionParticipant, "id" | "claimed_at"> & {
  /** A live join grant holds this seat. Absent on a roster too old to say. */
  claimed_by_grant?: boolean;
};

/**
 * Whether a name in the seat picker belongs to a phone that is not this one.
 *
 * On a QR night the claim is what makes blind entry true: a seat is held by
 * exactly one device, and a second device rating as that person publishes an
 * answer under their name that their own screen then shows back to them as
 * theirs. Nobody at the table can tell that happened. So a claimed seat is
 * offered to the device that claimed it and to no one else.
 *
 * `claimed_at` cannot answer that on its own, because two different things set
 * it: a guest claiming the seat, and the server's hold on the seat the host is
 * playing on. The roster now says which — `claimed_by_grant` — and the two get
 * opposite answers:
 *
 *  - **A grant holds it.** Somebody's phone, and only the device whose own grant
 *    names that seat may sit in it. A remembered id does not count and never
 *    did: `sessionStorage` is this tab's opinion, and it was wrong exactly when
 *    a guest had since taken the seat this tab remembers.
 *  - **The host's hold.** The host's own device, whatever this tab remembers, may
 *    sit back down in it — a fresh tab has no memory and no grant, and locking
 *    the host out of their own seat is the failure this exists to stop. The host
 *    could always write it (the server allows the owner every non-granted seat);
 *    only the picker disagreed.
 *
 * A roster that says nothing falls back to the old rule, which is the safe half:
 * a claimed seat that is not the remembered one reads as taken.
 *
 * A shared device is the opposite case and stays unlocked. There the phone *is*
 * the table and passing it around is the entire interaction, so every seat is
 * tappable however many of them some other device has claimed.
 */
export function seatTakenElsewhere(input: {
  /** The phrasing, not the session's mode: a guest phone is its own device. */
  phrasing: CaptureMode;
  /** A grant makes this a borrowed phone; without one it is the host's own browser. */
  role?: Role;
  /** The one seat a borrowed phone may ever hold. */
  grantSeatId?: string | null;
  ownSeatId: string | null;
  seat: Seat;
}): boolean {
  if (input.phrasing === "SHARED_DEVICE") return false;
  if (input.seat.claimed_at === null) return false;
  if (input.seat.claimed_by_grant === true) return input.seat.id !== (input.grantSeatId ?? null);
  if (input.seat.claimed_by_grant === false && (input.role ?? "owner") === "owner") return false;
  return input.seat.id !== input.ownSeatId;
}

/**
 * The one seat this device may hold even after the roster says it is claimed.
 *
 * A grant is the strongest form of that: it names the single seat a borrowed
 * phone may ever write, and it is the seat the server itself checks. The host's
 * own device has no grant, so the remembered pick stands in — which this
 * deliberately did *not* do until now, on the reasoning that `saveSeat` records
 * a local choice the server never hears about, so a seat coming back claimed
 * must have been claimed by some other phone.
 *
 * That reasoning has expired. The server now takes a hold on the host's own seat
 * as soon as the host names it with `?as=` — that is what stops the join screen
 * offering the host's name to a guest — so "claimed" no longer means "elsewhere",
 * and a grant-only answer ejected the host from the seat it had just tapped.
 *
 * This is not what enforces the blind guarantee any more, and does not need to
 * be: the server refuses a write to a seat held by a live join grant, keyed on
 * the `participant_id` in the body. A remembered seat that a guest has since
 * claimed is refused by `seatTakenElsewhere` before it gets that far — the
 * roster now says which seats a grant holds — so the collision no longer even
 * reaches the server.
 */
export function ownSeat(input: {
  grantSeatId: string | null;
  rememberedSeatId: string | null;
}): string | null {
  return input.grantSeatId ?? input.rememberedSeatId;
}

/**
 * The one extra sentence the "Not sent" banner adds to its own lead-in.
 *
 * The banner already opens with "…has not reached the table yet", and the app's
 * fallback for an unexplained failure opens by saying the same thing, so the
 * notice said it twice in a row. Only the half that carries something new is
 * kept here.
 *
 * A sentence the server wrote for a person is worth all of itself — "this game
 * night is already finished" and "check your signal" call for opposite
 * responses, and only the server knows which one this is. Everything else gets
 * the app's own line: a transport that never got an answer has nothing to quote,
 * and a status code read out at a game table names nothing anybody there can do.
 */
export function unsentDetail(err: unknown): string {
  return serverSentence(err) ?? "This phone cannot hold it, so keep this screen open.";
}

/** The server's own words for a failure, or null where it wrote none. */
function serverSentence(err: unknown): string | null {
  const cause = err instanceof Error ? err.cause : null;
  for (const candidate of [err, cause]) {
    if (candidate instanceof ApiError && candidate.serverSaid) return candidate.message;
  }
  return null;
}

/**
 * The one extra sentence the "Not saved" banner adds.
 *
 * A refusal is a different thing from a failure to send, and the difference is
 * the whole point: nothing is coming back for this one, so the line must not
 * borrow the unsent line's promise that the screen is worth keeping open.
 *
 * The server's own sentence carries the part a person at the table has to act
 * on, and the two they actually meet want opposite things — "that answer is
 * already revealed" means the table has settled it and there is nothing to do,
 * "that seat is on another device" means the name they are rating under is
 * somebody else's phone and they need their own. Neither is a status code, and
 * neither is something this file is in a position to work out.
 */
export function refusedDetail(err: unknown): string {
  return serverSentence(err) ?? "The table would not take it, and asking again will not change it.";
}

/** English counts, so a notice at a table reads as a sentence and not a log. */
const COUNT_WORDS = [
  "No",
  "One",
  "Two",
  "Three",
  "Four",
  "Five",
  "Six",
  "Seven",
  "Eight",
  "Nine",
  "Ten",
];

/** Close a clause the server left open, without doubling one it did not. */
function sentence(text: string): string {
  const trimmed = text.trim();
  return /[.!?]$/.test(trimmed) ? trimmed : `${trimmed}.`;
}

function nameList(names: readonly string[]): string | null {
  if (names.length === 0) return null;
  if (names.length === 1) return names[0] ?? null;
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

/**
 * The "Not saved" notice, naming what was lost rather than counting it.
 *
 * What this replaces was a number next to a reason: "3 ratings did not go on the
 * table: that answer is already revealed." Three of what? The only thing tying
 * the count to an answer was the cell key, which is two ULIDs and is not
 * something anybody at a table can read, so the badge was undecodable by design
 * and the person holding the phone had no way to know which questions to raise
 * with the room.
 *
 * The titles were being collected all along — `Outgoing.label` is the step's
 * own name — and thrown away one branch short of the screen. So they are said,
 * in the order they were refused, and the server's sentences are said after
 * them, de-duplicated: three refusals of one kind are one reason, not three.
 */
export function refusedLine(
  refused: readonly { label?: string; message?: string }[],
): string {
  const count = COUNT_WORDS[refused.length] ?? String(refused.length);
  const noun = refused.length === 1 ? "rating" : "ratings";
  const names = nameList(refused.flatMap((o) => (o.label ? [o.label] : [])));
  // The server writes a clause, not a sentence — "that answer is already
  // revealed" — and it is being read next to two of the app's own, so it is
  // closed here rather than left to run into the line after it.
  const reasons = [
    ...new Set(refused.flatMap((o) => (o.message ? [sentence(o.message)] : []))),
  ];
  const what = names === null ? `${count} ${noun}` : `${count} ${noun} (${names})`;
  // No reason to give is a full stop, not a colon in front of nothing.
  const why = reasons.length === 0 ? ". " : `: ${reasons.join(" ")} `;
  return `${what} did not go on the table${why}Nothing is waiting to send.`;
}

// ---------------------------------------------------------------------------
// When a rating counts as settled
// ---------------------------------------------------------------------------

/**
 * How long a value has to stand still before it is worth telling the table.
 *
 * The gem reports every stop the thumb crosses, because the readout and the
 * fill have to follow the thumb. Only the last of them is the answer. This is
 * the window in which "the last one" is decided, and it has to be longer than
 * every gap a machine produces on its own:
 *
 *  - a pointer drag raises events a frame apart, ~16ms;
 *  - a held arrow key repeats after the OS's delay-until-repeat, which bottoms
 *    out near 225ms on macOS and 250ms on Windows and defaults to twice that.
 *
 * 300ms clears both, and is short enough that the write is on its way before
 * the phone has left the rater's hand. It is not a promise, either: every
 * boundary a person can cross — Next, Back, the hand-off chip, the results
 * button, leaving the screen — forces the value out early, so the only way to
 * actually wait 300ms is to sit still for 300ms.
 */
export const SETTLE_MS = 300;

/** The timer the settler runs on. Injected so the policy is testable. */
export interface SettleClock<H = ReturnType<typeof setTimeout>> {
  set(run: () => void, ms: number): H;
  clear(handle: H): void;
}

export interface Settler {
  /**
   * The rater has landed on a value for now. Supersedes anything still waiting:
   * a value dragged *through* is discarded here and never reaches the server.
   *
   * `key` is the cell it belongs to, so that a late answer about one rating
   * cannot throw away a different one the rater has since started.
   */
  offer(key: string, run: () => void): void;
  /** Send what is waiting, now. True if anything went. */
  flush(): boolean;
  /**
   * Throw away what is waiting, unsent — because something newer replaces it.
   * With a key, only if what is waiting is that cell's.
   */
  drop(key?: string): void;
  waiting(): boolean;
}

/**
 * One value in flight at a time, and it is the one the rater stopped on.
 *
 * Deliberately a quiet window rather than a pointerup hook inside `Gem`. Three
 * reasons, in order of weight:
 *
 *  1. A pointer release is not the only way a rating settles. An arrow key has
 *     no release at all — a screen-reader user nudging along the ladder would
 *     have kept every one of the intermediate writes, which is the same bug with
 *     a smaller audience and no way to see it. A window is the same rule for a
 *     thumb, a key, a chip tap and an assistive "set value".
 *  2. `Stars` is a controlled component and every guarantee it carries — blind
 *     concealment, no default position, the post-reveal lock — is derived from
 *     its props in `starView`. A settled value held inside it would be a second
 *     source of truth sitting behind all three.
 *  3. This screen already owns the write boundary. `commit(boundary)` is called
 *     everywhere the table moves on, and is where an answer the rater has
 *     finished making is forced out.
 *
 * The trade this makes is honest and small: for up to `SETTLE_MS` a rating
 * exists only on this device. The screen shows it immediately — the draft is set
 * on every emission, so nothing about the feel of a drag changes — and the
 * window is closed by every boundary a person can cross, including leaving the
 * page.
 */
export function createSettler<H>(delayMs: number, clock: SettleClock<H>): Settler {
  let timer: H | null = null;
  let pending: { key: string; run: () => void } | null = null;

  /** Disarm and hand back whatever was waiting. */
  const take = (): (() => void) | null => {
    if (timer !== null) clock.clear(timer);
    timer = null;
    const held = pending;
    pending = null;
    return held?.run ?? null;
  };

  return {
    offer(key, run) {
      take();
      pending = { key, run };
      timer = clock.set(() => {
        // Disarmed before running, so a `drop()` from inside the write itself
        // finds nothing to cancel rather than re-entering this.
        const held = take();
        held?.();
      }, delayMs);
    },
    flush() {
      const run = take();
      if (!run) return false;
      run();
      return true;
    },
    drop(key) {
      if (key !== undefined && pending !== null && pending.key !== key) return;
      take();
    },
    waiting() {
      return pending !== null;
    },
  };
}

/** The real clock. Held apart from the policy so tests never wait on one. */
const browserClock: SettleClock = {
  set: (run, ms) => setTimeout(run, ms),
  clear: (handle) => clearTimeout(handle),
};

// ---------------------------------------------------------------------------
// Small pure helpers
// ---------------------------------------------------------------------------

/**
 * Where this screen belongs once the night is over, or null while it is not.
 *
 * A COMPLETED session refuses every rating, so leaving the live flow up offers
 * controls that write to the device and are then turned down — which is also how
 * a phone ends up holding rows the server never took. Browser-Back off the
 * results screen lands here on an ordinary evening, so this is not an exotic
 * path worth guarding with a disabled button somewhere.
 *
 * The status is the one on the server's read model, not a local row. That is
 * deliberate: it is the same row the write path checks before refusing, so the
 * screen and the refusal cannot disagree about whether the night is finished.
 * An absent status is a state that has not been read yet and is guessed neither
 * way — "closed" would bounce a live table off its own rating screen.
 */
export function finishedNightRoute(
  sessionId: string,
  status: SessionStatus | null,
): string | null {
  if (sessionId === "" || status !== "COMPLETED") return null;
  return `/session/${sessionId}/results`;
}

/** What the footer's forward button does when it is pressed. */
export type ForwardKind =
  /** Shared device: this rater is finished, the phone goes to the next person. */
  | "pass"
  /** The table moves to the next step, with a skip row for the rater if owed. */
  | "next"
  /** The table moves on and the people still waiting never answer this one. */
  | "leave"
  /** Out of the loop and into the results. Writes nothing. */
  | "results";

export interface Forward {
  kind: ForwardKind;
  label: string;
  /** How loud the button is. `data-variant`, straight through to the markup. */
  variant: "primary" | "quiet";
  disabled?: boolean;
}

/**
 * The one forward control at the bottom of the screen, for every state this
 * loop has — including the one it used to get catastrophically wrong.
 *
 * The footer renders on every state of this screen, hand-off included, and the
 * ternary this replaces never asked whether anybody was sitting down. On the
 * hand-off screen — no actor, the seat picker filling the page above — it fell
 * through to a `data-variant="primary"` **Next**: the loudest control on a
 * screen whose entire job is "choose who rates next", one thumb-width from the
 * bottom edge. Pressing it walked the whole table past the step. It also wrote
 * nothing on the way, because `skipWrite` needs an actor and there is none here,
 * so the people who had not had their turn simply never got one — and the step
 * they were on stays blind for good, since the reveal is a count of committed
 * rows against the roster and that count can no longer be reached. QA lost two
 * aspects to it before noticing what the button had done.
 *
 * The rule that replaces it is one sentence: **while a seat is being picked,
 * the footer does not move the table unless moving costs nobody anything.**
 * That is three cases and no more.
 *
 *  - *Nobody is waiting on this step.* Advancing takes nothing from anyone, so
 *    it stays the loud button it has always been. This is the ordinary end of a
 *    shared-device round — the last rater has just passed the phone back, and
 *    going to the next question is exactly what the table wants next.
 *  - *Somebody is waiting, and the phone belongs to the table.* Moving on is a
 *    real decision with a cost, so it is offered quietly and named for its cost
 *    rather than called "Next". It is not removed: a table that has collectively
 *    decided it does not care about Artwork has to be able to leave it, and Back
 *    still comes back to it for as long as the night is open.
 *  - *Somebody is waiting, and this is one person's own phone.* Nothing is
 *    offered. This device cannot answer for anybody until it holds a seat, so
 *    the only honest forward motion is picking one — which is what the screen is
 *    already asking for. Returns null and the footer renders Back alone. A guest
 *    whose seat has gone lands here too, and "scan the code again" is likewise
 *    the only thing left to do.
 *
 * The results are the exception in every state, because opening them writes
 * nothing and the gate on them is the server's `canFinish` rather than this
 * file's opinion. They go quiet on a hand-off that is leaving people behind, for
 * the same reason as everything else on that screen.
 *
 * Nothing here writes, and in particular the quiet move-on does **not** put n/a
 * rows in for the people it leaves behind. The call site says why it must not.
 */
export function forwardAction(input: {
  phrasing: CaptureMode;
  /** No actor: the seat picker is on screen, and this is a hand-off. */
  needsSeat: boolean;
  /** Who this step is still waiting on, by name, in roster order. */
  waiting: readonly string[];
  isLast: boolean;
  /** The server's word that the headline is in and the results will open. */
  canFinish: boolean;
  /** Whether the rater holding the phone has put something on this step. */
  answered: boolean;
}): Forward | null {
  const { phrasing, needsSeat, waiting, isLast, canFinish, answered } = input;
  // "Free" as in costs nobody anything: every seat at the table is already in,
  // so moving on leaves no answer behind and needs no apology.
  const free = waiting.length === 0;

  if (!needsSeat && phrasing === "SHARED_DEVICE" && !free) {
    return {
      kind: "pass",
      label: answered ? "Done — pass it on" : "Skip — pass it on",
      variant: "primary",
    };
  }
  if (isLast) {
    return {
      kind: "results",
      label: "See the results",
      variant: needsSeat && !free ? "quiet" : "primary",
      disabled: !canFinish,
    };
  }
  if (!needsSeat || free) return { kind: "next", label: "Next", variant: "primary" };
  if (phrasing !== "SHARED_DEVICE") return null;
  return { kind: "leave", label: leaveLabel(waiting), variant: "quiet" };
}

/**
 * The two controls only the host gets, and only where they are any use.
 *
 * Both were missing for the same reason: this screen was written for the phone
 * being passed around the table, and everything a host might need in the middle
 * of a QR night was left on a screen a QR host never sees.
 *
 * **The way back to the seat picker** was drawn on `phrasing ===
 * "SHARED_DEVICE"` alone, on the reading that a seat picked once on a phone of
 * one's own never changes. But the picker only shows while there is no actor,
 * and nothing else on the screen clears one — so a host who tapped the wrong
 * name went on publishing under it for the rest of the evening, and the person
 * whose name it was watched answers appear as their own. One mis-tap, no undo.
 * It is the host's on either kind of night now, and never a guest's: a borrowed
 * phone's seat is its grant rather than a choice, and clearing the actor there
 * lands on "no seat, scan again" with the grant still naming the same seat.
 *
 * **Taking a name back off a phone** already existed, on the seat picker, for
 * the lockout it was written for — the host has no seat because a guest took
 * their name. The night that cannot finish is a different one and never passes
 * through that screen: the host has a seat, is in the middle of the loop, and
 * the roster says "Waiting" next to a name whose phone is in a coat in another
 * room. The reveal counts committed rows, so it never comes.
 *
 * Which seats are offered is deliberately narrower than "every seat a guest
 * holds". A phone that has already answered is not holding anything up, and
 * offering to take its name back is offering to throw away an answer that is
 * doing no harm. Nothing is offered once the step is revealed either — a
 * revealed answer is settled, and the server refuses to discard it.
 */
export function hostControls<P extends { id: string; claimed_by_grant?: boolean }>(input: {
  role: Role;
  /** This step's reveal, as the server gave it. */
  revealed: boolean;
  participants: readonly P[];
  /** Who has a committed row on this step. */
  committed: ReadonlySet<string>;
}): { seatChange: boolean; takeBack: P[] } {
  const host = input.role === "owner";
  return {
    seatChange: host,
    takeBack:
      host && !input.revealed
        ? input.participants.filter(
            (p) => p.claimed_by_grant === true && !input.committed.has(p.id),
          )
        : [],
  };
}

/**
 * What the quiet way past an unanswered step is called.
 *
 * Names the one person it leaves behind, because at a table of four with one
 * holdout "Move on without Ana" is a sentence somebody says out loud and the
 * room can answer. Past one name it stops naming: the button is half a footer
 * wide in uppercase mono, and the full list belongs in the hint above it, which
 * has the room to say what moving on actually costs.
 */
function leaveLabel(waiting: readonly string[]): string {
  return waiting.length === 1 ? `Move on without ${waiting[0]}` : "Move on without them";
}

/** One person's answer to one step, as it exists on this device right now. */
export interface Draft {
  key: string;
  value: number | null;
  na: boolean;
  note: string;
}

/** A write in flight, or a write that failed and is still owed to the table. */
interface Outgoing {
  key: string;
  participantId: string;
  label: string;
  target: RatingTarget;
  message?: string;
  /**
   * Set on the entries this screen did not push itself — a rating the outbox
   * carried away, pushed later, and had refused out of sight. They are shown in
   * the same notice as the rest, but the truth about them lives in the
   * sidelined-op list, so the op is named here: the notice stands only while
   * that op is still set aside and this screen has not written the cell again
   * since. See `outboxNoticeStale`.
   */
  outboxOp?: { opId: string; at: number };
}

// ---------------------------------------------------------------------------
// When a draft is allowed to become a row
// ---------------------------------------------------------------------------

/**
 * The two kinds of moment at which this screen considers writing a draft down.
 *
 * The distinction is the fix for a real corruption, so it is worth stating why
 * it exists rather than trimming it away later. "The rater has stopped touching
 * things for a moment" and "the rater is finished with this answer" are not the
 * same event, and this screen used to treat them as one: the note field wrote on
 * `blur`, and a browser blurs the textarea as the *default action of the
 * mousedown* that lands on a rating stop. So typing words and then tapping a
 * number — the most natural order there is, because the words are why the number
 * is hard — sent the words first, on their own. An answer with no number is
 * stored as n/a, an n/a is a committed row, a committed row can be the one that
 * completes the count, and the reveal is a count. The number the rater actually
 * tapped then arrived at a cell the server had already closed.
 *
 * A `"settle"` is therefore the rater pausing while still on this cell — focus
 * moving out of the note field, a value standing still — and a note with no
 * number is not written at one. A `"leave"` is the rater going: the next step,
 * the next seat, the results screen, the tab. Only then is a note on its own all
 * the answer there is ever going to be, and only then is writing it right.
 */
export type Boundary = "settle" | "leave";

/**
 * Two answers that would land as the same row.
 *
 * Notes compare trimmed because `aspectTarget` trims before it writes, so a
 * trailing space is not an edit the table can even see, and a write for one is a
 * round trip that can only fail — which on this screen means a "Not saved"
 * notice about a rating that is in fact saved.
 *
 * "No number" and "doesn't apply" compare equal for the same reason: they are
 * the same row. The database accepts only `(na = 1 AND value IS NULL)` or
 * `(na = 0 AND value IS NOT NULL)`, so an answer with no number comes back from
 * the table as n/a whatever it was called on the way there — and a screen that
 * read those two as different answers would write one row twice, which is a
 * round trip that can be refused *because the first one landed*.
 */
export function sameAnswer(
  a: { value: number | null; na: boolean; note: string | null },
  b: { value: number | null; na: boolean; note: string | null },
): boolean {
  const stored = (x: { value: number | null; na: boolean }): boolean => x.na || x.value === null;
  return (
    a.value === b.value &&
    stored(a) === stored(b) &&
    (a.note ?? "").trim() === (b.note ?? "").trim()
  );
}

/**
 * Whole means the row carries the thing an answer is: a number, or the
 * deliberate "this one doesn't apply". Words alone are not, at least not until
 * the rater's last word on the cell — which is `boundaryWrite`'s business, not
 * this one's. Named once because two callers ask it and they must not come to
 * different conclusions.
 */
function wholeAnswer(draft: { value: number | null; na: boolean }): boolean {
  return draft.value !== null || draft.na;
}

/**
 * Whether the draft in front of the rater should go to the table at this
 * boundary. The whole of the screen's write policy, in one pure function —
 * with one gate in front of it, and only one: a replacement of a sealed answer
 * that nobody has agreed to yet never reaches here at all. See
 * `replacementHeld`, which `commit` asks first and which answers a different
 * question — not "is this worth writing" but "may this be written over".
 *
 * Three separate refusals, each of which was a bug on its own:
 *
 *  1. Half an answer is not a row. A note with no number is a draft while the
 *     rater is still here — see `Boundary`. This is the reveal gate's protection
 *     and it belongs here rather than at the gate, because the gate is the
 *     server's and the server cannot tell a deliberate "I only have words for
 *     this" from a sentence typed on the way to a number.
 *  2. What the table already holds is not news. A rating dragged away from the
 *     stored value and back again is not a write.
 *  3. What this screen has already handed to the transport is not news either.
 *     Without this, a lone note left through the note field's own boundary and
 *     then again through Next — two writes of one answer, the second of which
 *     can be refused *because the first one landed*, which is how a screen ends
 *     up saying "Not saved" about a rating that is on the table.
 *
 * `sent` is the last answer written for this cell, not the last one the server
 * confirmed. That is deliberate: the point is to not write the same thing twice,
 * and a write still in flight has already been said once. A genuine failure is
 * kept and named by `push` on the other side of it, which is a different job.
 */
export function boundaryWrite(input: {
  draft: Draft | null;
  cellKey: string;
  committed: { value: number | null; na: boolean; note: string | null } | null;
  sent: Draft | null;
  boundary: Boundary;
}): boolean {
  const { draft, cellKey, committed, sent, boundary } = input;
  // A draft for a cell the screen has moved off is somebody else's business, and
  // writing it here is how one step's answer lands on another's.
  if (draft === null || draft.key !== cellKey) return false;

  // Words alone are a whole answer too, but only at the boundary where they are
  // the rater's last word on it.
  if (!wholeAnswer(draft) && (boundary === "settle" || draft.note.trim() === "")) return false;

  if (committed !== null && sameAnswer(draft, committed)) return false;
  if (sent !== null && sameAnswer(draft, sent)) return false;
  return true;
}

/**
 * What the outbox is holding against this rating, in the server's own words.
 *
 * The shared-device write path does not fail in front of the rater. `mutate()`
 * writes the row to IndexedDB and resolves, the screen says "Saved on this
 * phone" — honestly, at that moment — and the push happens later, out of sight.
 * When the server refuses that push the op is set aside as a dead letter with
 * its reason, and this screen never read that list: the phone went on saying a
 * number was saved that the table had already closed the door on. A silent lie
 * about a durable write is worse than any of the rest of this bug, so the
 * refused op is read back and said in the same notice as a refusal that happened
 * in front of the rater.
 *
 * The last matching op wins. Two refusals of one cell mean the second attempt is
 * the one the rater made most recently, and it is their sentence to answer.
 *
 * `since` is when this screen last wrote that cell, and nothing older counts.
 * Dead letters are kept until somebody clears them from the sync screen, so
 * without that cut-off a refusal from an earlier sitting — a night since
 * finished, a seat since given back — would come back as a notice about the
 * answer in front of the rater now, with no way to dismiss it from here.
 */
export function outboxRefusal(
  entity: Entity,
  rowId: string,
  sidelined: readonly SidelinedOp[],
  since = 0,
): {
  opId: string;
  at: number;
  message: string;
  answer: { value: number | null; na: boolean; note: string | null };
} | null {
  const mine = sidelined.filter(
    (s) => s.op.entity === entity && s.op.id === rowId && s.at >= since,
  );
  const held = mine[mine.length - 1];
  if (!held) return null;
  const patch = held.op.patch as Record<string, unknown>;
  return {
    opId: held.op_id,
    at: held.at,
    // A dead letter carries the server's clause — "that answer is already
    // revealed" — which is the part somebody at the table can act on, and is
    // exactly what `refusedDetail` pulls out of a live refusal. A conflict
    // carries a row instead of a sentence, so this file has to write the
    // sentence, and the only true thing to say is that the answer moved
    // somewhere else.
    message:
      held.kind === "dead"
        ? (held.reason ?? refusedDetail(null))
        : "that answer changed on another device",
    // Rebuilt from the patch so the notice names a real answer. The column is
    // SQLite's, so `na` arrives as 0 or 1 rather than as a boolean.
    answer: {
      value: typeof patch.value === "number" ? patch.value : null,
      na: patch.na === 1 || patch.na === true,
      note: typeof patch.note === "string" ? patch.note : null,
    },
  };
}

/**
 * When a refusal read out of the outbox has stopped being true.
 *
 * Two ways, and both of them happened in review before they happened at a table.
 * The op can be gone from the sidelined list — resolved, or discarded from the
 * sync screen — in which case there is nothing left to report. Or this screen can
 * have written that cell again *after* the refusal, in which case the notice is
 * about a superseded attempt: the newest write is the one the rater is waiting to
 * hear about, and it will name its own failure if it has one.
 *
 * Kept separate from the effect that uses it because a notice that cannot be
 * cleared is the kind of bug that only shows up on the fourth question of a real
 * night, which is a terrible place to find out.
 */
export function outboxNoticeStale(
  entry: { opId: string; at: number },
  stillSidelined: ReadonlySet<string>,
  lastWriteAt: number,
): boolean {
  return !stillSidelined.has(entry.opId) || lastWriteAt > entry.at;
}

/** A rating row read back out of this device's own store. */
export interface LocalRow {
  value: number | null;
  na: boolean;
  note: string | null;
}

interface LocalCache {
  key: string;
  rows: Map<string, LocalRow>;
}

const NO_LOCAL: LocalCache = { key: "", rows: new Map() };

function targetFor(step: Step, draft: Draft): RatingTarget | null {
  if (step.kind === "headline") {
    return draft.value === null ? null : { kind: "headline", value: draft.value };
  }
  // `aspectTarget` / `subQuestionTarget` own the one rule the database will
  // accept: no number means n/a, whether that is a skip, a "doesn't apply" or a
  // sentence typed instead.
  return step.kind === "subquestion"
    ? subQuestionTarget(step.view.subquestion.id, draft)
    : aspectTarget(step.view.aspect.id, draft);
}

function toLocalRow(row: Record<string, unknown>): LocalRow | null {
  if (row.deleted === 1) return null;
  return {
    value: typeof row.value === "number" ? row.value : null,
    na: row.na === 1 || row.na === true,
    note: typeof row.note === "string" && row.note !== "" ? row.note : null,
  };
}

/**
 * Whether a committed answer may be read back to whoever is holding this phone.
 *
 * The narrower of the two questions this screen asks about a seat, and the one
 * that was never asked. Which seat this device is sitting in is `actorId`, and
 * that is not in doubt. Whether the answer already in that seat may go back on
 * the screen is this, and on a shared device it is nobody's.
 *
 * A shared device cannot distinguish its players. One phone goes round the table
 * and every player rates through the same owner session, so the seat is not a
 * fact about the device: it is whatever name was tapped last, and the next tap
 * can be any other name. `readSessionState` stopped serving an unrevealed value
 * to that reader for exactly this reason — and half the leak was on this side of
 * the wire, which is why the server fix alone did not close it. On a shared
 * device the write path is the outbox, so every blind value the table produced
 * all evening is sitting in this phone's own IndexedDB, and this file used to
 * unredact that replica by `actorId` alone. Tapping a name therefore still handed
 * over that person's committed answer with no server involved: offline for the
 * whole evening, and online in the window between a write and the next pull.
 *
 * A grant is the fact that makes the read safe on the other kind of night. It
 * binds one seat to one phone, so a guest reading its own answer back is the
 * person whose answer it is, and the host of a QR night has taken the same hold
 * on the seat it named. Nothing changes there. So the gate is written the
 * positive way round — readable only where a seat is bound to a device — and a
 * session whose mode this screen does not know yet reads as not readable rather
 * than as permission, which is the same direction `mode` itself defaults in.
 *
 * The seat is untouched, deliberately, and the server commit says why at more
 * length: `actor_participant_id` is how this screen knows which seat it is in and
 * the seat picker is built on it, so blanking that would eject the host from
 * their own seat rather than close a leak. Blind *overwriting* is untouched too —
 * a player who changes their mind still writes over their own answer, which is
 * the pass-the-phone correction. It is blind *re-reading* that stops.
 */
export function ownAnswerReadable(mode: CaptureMode, role: Role): boolean {
  if (role === "guest") return true;
  return mode === "MULTI_DEVICE";
}

/**
 * The seat's own committed answer, split into the two things the screen wants of
 * it: *that* it answered, and *what* it said.
 *
 * They come apart on a shared device, and keeping them apart is the whole fix.
 * That a seat has answered is not a leak, and several things depend on knowing
 * it: the roster says "In", `forwardAction` offers the phone on instead of a
 * skip, and — the one that would cost a real answer — `skipWrite` must not put an
 * empty n/a over a rating that is already on the table. What the seat said is the
 * leak, and it goes, from the server's row and from this device's own replica
 * alike, because the replica is where it was actually sitting.
 *
 * `shown` is also what `boundaryWrite` compares against, and null there means the
 * dedupe is off rather than that the cell is empty. That is the honest reading:
 * "what the table already holds is not news" is only an answer this screen can
 * give when it is allowed to know what the table holds. Passing the real value in
 * for the comparison alone would keep a slow oracle open — enter a number, watch
 * whether a write goes out — and would buy nothing, because that rule exists for
 * a rater who drags away from the value on screen and back onto it, and on a
 * shared device there is no value on screen to drag away from.
 *
 * The server's `visible` flag is read rather than second-guessed. It already
 * means "revealed, or this seat's own where that is safe", so the reveal handing
 * everyone their answer back — including the reader's, under a gem that is locked
 * by then — needs no rule of its own here.
 */
export function ownAnswer(input: {
  readable: boolean;
  local: LocalRow | null;
  server: { visible: boolean; value: number | null; na: boolean; note: string | null } | null;
}): { committed: boolean; shown: LocalRow | null } {
  const { readable, local, server } = input;
  const committed = local !== null || server !== null;
  if (readable && local !== null) return { committed, shown: local };
  if (server !== null && server.visible) {
    return { committed, shown: { value: server.value, na: server.na, note: server.note } };
  }
  return { committed, shown: null };
}

/**
 * A cell holding an answer this screen is not allowed to read back.
 *
 * The two halves of `ownAnswer`, recombined into the one state the controls have
 * to say something about: somebody in this seat has answered, and what they said
 * is gone until the reveal. It is the normal state of every rated cell on a
 * shared-device night, and the screen used to have no word for it — an empty
 * control reading "Not rated", which is not a blank slate but a wrong answer, and
 * a re-tap on it wrote over a stored rating with no sign that anything had been
 * there.
 */
export function sealedCell(own: { committed: boolean; shown: LocalRow | null }): boolean {
  return own.committed && own.shown === null;
}

/** A draft that says something: a number, a "doesn't apply", or words. */
function drafted(draft: Draft | null, cellKey: string): boolean {
  if (draft === null || draft.key !== cellKey) return false;
  return draft.value !== null || draft.na || draft.note.trim() !== "";
}

/**
 * What a confirmed replacement actually writes.
 *
 * "Replace it" means replacing what the rater put their hands on, not clearing
 * the parts of the row they were never shown. A cell is one row and the whole of
 * it is hidden, so a re-tap that only moves the stars arrives here with an empty
 * note field — not because anybody deleted the sentence, but because the field
 * could not show it. Writing that draft as it stands would take the words with
 * the number, which is the same silent loss this gate was built to stop, one
 * layer down.
 *
 * So each half of the answer is carried forward only where the rater said
 * nothing: a draft with no number keeps the stored number, a draft with no words
 * keeps the stored words. Both are read for the merge alone and neither is ever
 * displayed — the row is the one this device wrote, sitting in its own replica,
 * and reading it to write it back is not the same as putting it on screen.
 *
 * The two things a rater *can* say are both positive acts and both survive:
 * "doesn't apply" is an n/a with no number and is written as one, and a sentence
 * typed here replaces whatever sentence was there. What they cannot do on a
 * blind night is empty a field they cannot see — deleting nothing and deleting
 * something look identical from here, and of the two readings only this one
 * cannot lose an answer.
 *
 * `stored` is null when this screen has no replica of the row — a seat answered
 * on another device, say — and then there is nothing to merge and the draft goes
 * as it is.
 */
export function replacementRow(draft: Draft, stored: LocalRow | null): Draft {
  if (stored === null) return draft;
  const untouched = draft.value === null && !draft.na;
  return {
    ...draft,
    value: untouched ? stored.value : draft.value,
    na: untouched ? stored.na : draft.na,
    note: draft.note.trim() === "" ? (stored.note ?? "") : draft.note,
  };
}

/**
 * Whether the answer in front of the rater is a replacement that has not been
 * agreed to yet — the state in which the draft moves but the write is held.
 *
 * The control stays live, deliberately: pass-the-phone correction is a real
 * thing people do, and a sealed cell that refuses the finger teaches the table
 * that the app has lost their answer. So the tap lands, the stars move, and what
 * waits is the row. The gate is one per cell and covers all three of stars, n/a
 * and note, because they are one answer and one row — confirming a star and then
 * silently overwriting a saved sentence is the same bug wearing a different hat.
 *
 * Held is not the same as refused. `commit` discards a held replacement at a
 * `"leave"` boundary rather than writing it: crossing out of the cell without
 * answering the question is an answer to it, and the saved rating stands.
 */
export function replacementHeld(input: {
  sealed: boolean;
  confirmed: boolean;
  draft: Draft | null;
  cellKey: string;
}): boolean {
  const { sealed, confirmed, draft, cellKey } = input;
  if (!sealed || confirmed) return false;
  return drafted(draft, cellKey);
}

/**
 * Folds this device's unsynced rows into the step the server sent.
 *
 * The redaction rule is copied from the server rather than relaxed: a local row
 * the caller has not named as showable says that its rater is in, and says
 * nothing else. This device physically holds everyone's numbers on a
 * shared-device night, and the whole point of blind entry is that holding them
 * is not the same as being allowed to display them.
 *
 * The third argument is deliberately not "who is rating" but "whose row may be
 * shown" — the same narrowing `readSessionState` makes between `own` and
 * `visibleOwn`. On a shared device it is null and the seat being rated is
 * redacted exactly like everybody else's, because a phone going round a table
 * cannot tell which of its players tapped that name: a row unredacted by
 * `actorId` alone is a row handed to whoever happens to be holding the phone.
 */
export function withLocal(
  step: Step,
  rows: Map<string, LocalRow>,
  visibleActorId: string | null,
): Step {
  if (rows.size === 0) return step;
  const known = new Set(step.view.ratings.map((r) => r.participant_id));
  const extra: RatingView[] = [];
  for (const [participantId, row] of rows) {
    if (known.has(participantId)) continue;
    if (participantId === visibleActorId) {
      extra.push({
        participant_id: participantId,
        committed: true,
        visible: true,
        value: row.value,
        na: row.na ? 1 : 0,
        note: row.note,
      });
    } else {
      extra.push({ participant_id: participantId, committed: true, visible: false });
    }
  }
  if (extra.length === 0) return step;
  const ratings = [...step.view.ratings, ...extra];
  // Spelled out per kind rather than spread over the union: each `view` is a
  // different shape and only the narrowed branch keeps its own key.
  if (step.kind === "headline") return { ...step, view: { ...step.view, ratings } };
  if (step.kind === "subquestion") return { ...step, view: { ...step.view, ratings } };
  return { ...step, view: { ...step.view, ratings } };
}

/**
 * What one revealed cell says.
 *
 * The rating leads as stars, because that is what the rater was handed. The
 * rubric's written label follows it where the rubric has one — it is the only
 * vocabulary this app has for what a position on the scale *meant*, and "3.5"
 * on its own describes no evening anybody had.
 */
function readValue(step: Step, cell: Cell): string {
  if (!cell.committed) return "No answer";
  // The note is read before `na`, not after: a skip and an answer given in
  // words are both stored as n/a, and only the note tells them apart.
  if (cell.value === null) {
    if (cell.note) return "Said it in words";
    return cell.na ? "Doesn't apply" : "No answer";
  }
  const stars = formatStars(cell.value) ?? String(cell.value);
  const label = starLabel(stopsOf(step), cell.value);
  return label ? `${stars} · ${label}` : stars;
}

// ---------------------------------------------------------------------------
// Pieces
// ---------------------------------------------------------------------------

function Notice(props: {
  tone: "wait" | "alert" | "ok";
  strong?: string;
  children: ReactNode;
}): ReactNode {
  return (
    <div className="pb-notice" data-tone={props.tone}>
      {props.strong ? <span className="pb-notice__strong">{props.strong}</span> : null}
      <span>{props.children}</span>
    </div>
  );
}

function Swatch(props: { colour: string }): ReactNode {
  // The swatch carries the profile's own colour, which is data rather than a
  // decision this file gets to make — hence the inline value.
  return <span className="pb-swatch" style={{ background: props.colour }} />;
}

function SeatChip(props: {
  participant: SessionParticipant;
  /** Held by another phone: shown, named and not tappable. */
  taken?: boolean;
  onPick: () => void;
}): ReactNode {
  const taken = props.taken === true;
  return (
    <button
      type="button"
      className="pb-chip"
      data-on="false"
      // Greyed rather than dropped from the list. That person is still sitting
      // at the table, and a name that quietly vanished reads as the app having
      // lost them — the tap is refused, the reason is on the chip.
      disabled={taken}
      onClick={props.onPick}
    >
      <Swatch colour={props.participant.colour_snapshot} />
      {/* Wrapped rather than left as a bare text node: a 43-character name
          measured 429px against a 390px viewport and pushed the rest of the chip
          off the phone, and `text-overflow` has nothing to act on without an
          element of its own. Same markup as the already-safe chip in `Join.tsx`. */}
      <span className="pb-seat__name">{props.participant.name_snapshot}</span>
      {taken ? <span className="pb-chip__meta">on their phone</span> : null}
    </button>
  );
}

/** Who is in and who is not, with every value still concealed. */
function Waiting(props: { cells: Cell[] }): ReactNode {
  return (
    <div className="pb-roster">
      {props.cells.map((cell) => (
        <div className="pb-seat" key={cell.participant.id}>
          <Swatch colour={cell.participant.colour_snapshot} />
          <span className="pb-seat__name">{cell.participant.name_snapshot}</span>
          <span className="pb-seat__state" data-state={cell.committed ? "in" : "waiting"}>
            {cell.committed ? "In" : "Waiting"}
          </span>
        </div>
      ))}
    </div>
  );
}

/**
 * The spread, once the server has revealed it.
 *
 * A dot per rater in their own colour, the mean behind them and faint, and the
 * `n` written out. Never a bare mean: a 5 that is four people agreeing and a 5
 * that is two people at either end are different evenings, and the second one is
 * the one worth talking about.
 */
function Spread(props: { step: Step; cells: Cell[]; ratings?: RatingCell[] }): ReactNode {
  const { step, cells } = props;
  const stops = stopsOf(step);
  // The headline is a straight 1–10 ladder, so it profiles as evaluative; a
  // descriptive axis passes its own kind and loses the mean, by decision #25. A
  // sub-question has no kind of its own and inherits its aspect's, so a
  // follow-up under a descriptive axis is not averaged either.
  //
  // `ratings` is passed for a topic, whose numbers are not all in its own rows:
  // a rater who went into detail has an answer made of six follow-ups and no
  // `aspect_rating` at all. The rail and the dots have to be built from the same
  // set or the mean marker lands somewhere no dot is.
  const profile = aspectProfile(
    props.ratings ?? ratingCells(step.view),
    stops,
    step.kind === "headline"
      ? "EVALUATIVE"
      : step.kind === "subquestion"
        ? step.parentKind
        : step.view.aspect.kind_snapshot,
  );
  const dist = profile.distribution;

  if (dist.n === 0) {
    return <p className="pb-hint">Nobody put a number on this one.</p>;
  }

  const dots = cells.flatMap((cell) =>
    cell.visible && !cell.na && cell.value !== null ? [{ cell, value: cell.value }] : [],
  );

  // Two people on the same stop must both be visible, so identical values fan
  // out sideways instead of stacking into one dot that reads as a single rater.
  const total = new Map<number, number>();
  for (const dot of dots) total.set(dot.value, (total.get(dot.value) ?? 0) + 1);
  const seen = new Map<number, number>();

  return (
    <div className="pb-dist">
      {/* Decoration: every dot on this rail is also a named row above it. */}
      <div className="pb-dist__rail" aria-hidden="true">
        {/* One notch per whole star, so the rail reads against the control the
            rater was handed rather than against a rubric they never saw. */}
        {Array.from({ length: STAR_COUNT }, (_, i) => (
          <span
            key={i}
            className="pb-dist__notch"
            style={{ left: `${starFraction(fullValue(i + 1)) * 100}%` }}
          />
        ))}
        {dist.mean === null ? null : (
          <span className="pb-dist__mean" style={{ left: `${starFraction(dist.mean) * 100}%` }} />
        )}
        {dots.map((dot) => {
          const rank = seen.get(dot.value) ?? 0;
          seen.set(dot.value, rank + 1);
          const share = total.get(dot.value) ?? 1;
          const nudge = (rank - (share - 1) / 2) * 6;
          return (
            <span
              key={dot.cell.participant.id}
              className="pb-dist__dot"
              style={{
                left: `calc(${starFraction(dot.value) * 100}% + ${nudge}px)`,
                background: dot.cell.participant.colour_snapshot,
              }}
            />
          );
        })}
      </div>
      <div className="pb-dist__legend">
        <span className="pb-dist__modal">{profile.modalLabel ?? "—"}</span>
        <span className="pb-dist__n">
          n = {dist.n}
          {profile.na > 0 ? ` · ${profile.na} n/a` : ""}
        </span>
      </div>
      {dist.split ? (
        <p className="pb-hint">
          <span className="pb-split">Split</span> You watched different games. That is the bit worth
          arguing about.
        </p>
      ) : null}
    </div>
  );
}

/**
 * Everyone's answer in words, attributed.
 *
 * `ratings` is the topic case: on an aspect step `cells` already carry values
 * worked out from the follow-ups, and `Spread` would otherwise recompute the
 * profile from the stored rows and disagree with the dots drawn beside it.
 */
function Revealed(props: { step: Step; cells: Cell[]; ratings?: RatingCell[] }): ReactNode {
  const notes = props.cells.flatMap((cell) =>
    cell.visible && cell.note ? [{ cell, note: cell.note }] : [],
  );
  return (
    <>
      <div className="pb-roster">
        {props.cells.map((cell) => (
          <div className="pb-cell" key={cell.participant.id}>
            <Swatch colour={cell.participant.colour_snapshot} />
            <span className="pb-seat__name">{cell.participant.name_snapshot}</span>
            <span
              className="pb-cell__label"
              data-na={cell.na || !cell.committed || cell.value === null ? "true" : "false"}
            >
              {readValue(props.step, cell)}
            </span>
          </div>
        ))}
      </div>
      <Spread step={props.step} cells={props.cells} ratings={props.ratings} />
      {notes.length > 0 ? (
        <div className="pb-section">
          <span className="pb-label">What people said</span>
          {notes.map(({ cell, note }) => (
            <p className="pb-cell__note" key={cell.participant.id}>
              {cell.participant.name_snapshot}: {note}
            </p>
          ))}
        </div>
      ) : null}
    </>
  );
}

/**
 * The way out of the last lockout, and the only place in the app that throws an
 * answer away.
 *
 * The seat rule is unconditional on purpose: a name a phone has claimed belongs
 * to that phone, and the automatic reclaim stops dead the moment there is an
 * answer on it, because taking it then would hand the host exactly the blind
 * values the whole app promises it cannot show them. That leaves one real table
 * in a hole — the QR goes up before the host has picked a name, a guest taps the
 * host's name and rates on it, and the host has nowhere to sit for the four
 * hours the grant lives.
 *
 * The way out cannot be a transfer, so it is a discard, and the discard is
 * spelled out before it happens rather than reported after. **Nothing here ever
 * shows what was on the seat.** The confirmation is the control: two taps, the
 * second one under a sentence saying what actually goes.
 *
 * What goes is narrower than it once was, and the copy has to keep up with the
 * server. The discard used to be all-or-nothing: any revealed row on the seat
 * and the whole control refused, which turned away the one seat that ever needs
 * it — a phone that answered three steps, was read by the table, and then died
 * on the fourth. The server now walks the seat row by row and keeps whatever the
 * table has already read. So the promise here is two-sided: the blind answers go
 * and nobody ever sees them, and the answers already on the board stay on it.
 * Saying "everything is thrown away" would frighten a host out of the only
 * control that unsticks the night, and would be false about a board they can
 * see.
 *
 * Rendered only in the host's own browser. A borrowed phone carries a grant, the
 * server refuses the route to any bearer token, and putting the button on that
 * screen would be an invitation to try.
 *
 * Two different dead ends lead here and they are not the same sentence, which is
 * what `prompt` is for. On the seat picker the host has no seat because a guest
 * took their name; in the middle of the loop the host has a seat and the table
 * is waiting on a phone that is not going to answer. The mechanism is identical
 * — the seat comes free and its unread answers are thrown away — but "somebody scanned
 * in as the wrong person?" is the wrong question to ask somebody staring at a
 * reveal that will not come.
 */
function TakeBack(props: {
  /** Seats a guest phone is holding. Empty means there is nothing to offer. */
  seats: ParticipantView[];
  /** Why this is on screen. Defaults to the wrong-name case it was built for. */
  prompt?: { label: string; hint: string };
  /** The seat currently being taken back, while the call is in flight. */
  busy: string | null;
  error: string | null;
  onTakeBack: (participant: SessionParticipant) => void;
}): ReactNode {
  const [armed, setArmed] = useState<string | null>(null);
  const target = props.seats.find((p) => p.id === armed) ?? null;

  if (props.seats.length === 0) return null;

  if (target) {
    return (
      <div className="pb-section">
        <Notice tone="alert" strong={`Take ${target.name_snapshot} back?`}>
          Anything answered as {target.name_snapshot} that this table has not read yet gets
          thrown away. Nobody sees it, not even you. Whatever the table has already read stays
          on the board. Whoever was using that phone has to scan the code again and pick the
          right name.
        </Notice>
        {props.error ? (
          <Notice tone="alert" strong="Nothing changed">
            {props.error}
          </Notice>
        ) : null}
        <div className="pb-actions">
          <button
            type="button"
            className="pb-btn"
            data-variant="primary"
            disabled={props.busy !== null}
            onClick={() => props.onTakeBack(target)}
          >
            {props.busy === target.id ? "Taking it back…" : "Take the name back"}
          </button>
          <button
            type="button"
            className="pb-btn"
            disabled={props.busy !== null}
            onClick={() => setArmed(null)}
          >
            Leave it alone
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="pb-section">
      <span className="pb-label">
        {props.prompt?.label ?? "Somebody scanned in as the wrong person?"}
      </span>
      <p className="pb-hint">
        {props.prompt?.hint ??
          "You can take a name back off the phone that took it. Anything on it nobody has read yet is thrown away — not even you see it — and answers this table has already read stay on the board. That phone scans in again."}
      </p>
      <div className="pb-chips">
        {props.seats.map((p) => (
          <button
            key={p.id}
            type="button"
            className="pb-chip"
            data-on="false"
            onClick={() => setArmed(p.id)}
          >
            <Swatch colour={p.colour_snapshot} />
            Take back {p.name_snapshot}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * The hand-off. On a shared device this is the whole ritual: the screen names
 * one person, and nothing of theirs is fetched until they say they are holding
 * the phone.
 */
function HandOff(props: {
  phrasing: CaptureMode;
  step: Step;
  participants: ParticipantView[];
  role: Role;
  grantSeatId: string | null;
  ownSeatId: string | null;
  suggested: SessionParticipant | null;
  onPick: (participant: SessionParticipant) => void;
  /** The host's own escape hatch. Absent on a borrowed phone. */
  takingBack?: string | null;
  takeBackError?: string | null;
  onTakeBack?: (participant: SessionParticipant) => void;
}): ReactNode {
  const { phrasing, step, participants, role, grantSeatId, ownSeatId, suggested, onPick } = props;

  if (phrasing !== "SHARED_DEVICE") {
    const seats = participants.map((p) => ({
      participant: p,
      taken: seatTakenElsewhere({ phrasing, role, grantSeatId, ownSeatId, seat: p }),
    }));
    const free = seats.filter((s) => !s.taken).length;
    return (
      <div className="pb-section">
        <span className="pb-label">Which seat is yours?</span>
        <p className="pb-hint">
          Pick yourself once. Your answers stay yours; everyone else&rsquo;s stay hidden until the
          table is in. Names already scanned in on another phone are theirs, not yours to fill in.
        </p>
        <div className="pb-chips">
          {seats.map(({ participant, taken }) => (
            <SeatChip
              key={participant.id}
              participant={participant}
              taken={taken}
              onPick={() => onPick(participant)}
            />
          ))}
        </div>
        {free === 0 ? (
          // Better a dead end with a sentence than a seat borrowed from somebody
          // who is already answering on it.
          <Notice tone="alert" strong="No seat here">
            Every name at this table is on somebody else&rsquo;s phone. Rate on the phone you
            scanned in with, or ask whoever set the game up to add you.
          </Notice>
        ) : null}
        {/*
         * Directly under the wall a locked-out host hits: the greyed chips that
         * say "on their phone", and the notice that says every name is taken.
         * This is where somebody in that state looks, so it is where the only
         * thing that helps them lives.
         */}
        {role === "owner" && props.onTakeBack ? (
          <TakeBack
            seats={participants.filter((p) => p.claimed_by_grant === true)}
            busy={props.takingBack ?? null}
            error={props.takeBackError ?? null}
            onTakeBack={props.onTakeBack}
          />
        ) : null}
      </div>
    );
  }

  const others = participants.filter((p) => p.id !== suggested?.id);

  return (
    <div className="pb-section">
      {suggested ? (
        <>
          <div className="pb-tile" data-edge="spice">
            <div className="pb-tile__stack">
              <div className="pb-tile__title">Pass the phone to {suggested.name_snapshot}</div>
              <div className="pb-tile__meta">
                {suggested.name_snapshot} rates {step.title} next. Nobody else&rsquo;s answers are on
                this screen.
              </div>
            </div>
          </div>
          <div className="pb-actions">
            <button
              type="button"
              className="pb-btn"
              data-variant="primary"
              onClick={() => onPick(suggested)}
            >
              I&rsquo;m {suggested.name_snapshot} — my turn
            </button>
          </div>
        </>
      ) : (
        <p className="pb-hint">
          Everyone has answered {step.title}. Hand the phone on, or move to the next one.
        </p>
      )}
      {others.length > 0 ? (
        <>
          <span className="pb-label">{suggested ? "Someone else has it" : "Who has the phone?"}</span>
          <div className="pb-chips">
            {others.map((p) => (
              <SeatChip key={p.id} participant={p} onPick={() => onPick(p)} />
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The screen
// ---------------------------------------------------------------------------

export function Rating(props: ScreenProps): ReactNode {
  const sessionId = props.params.id ?? "";

  // A grant in sessionStorage is the whole definition of "this is a borrowed
  // phone": it names the one seat this device may write, and it is why the
  // outbox never runs here.
  const grant = useMemo(() => readGrant(sessionId), [sessionId]);
  const role: Role = grant ? "guest" : "owner";
  const token = grant?.token ?? null;

  // Held in state as well as in storage: the server marks the host's own seat
  // claimed within a poll of the host picking it, and a value only read at mount
  // would still be null when that poll lands.
  const [seatMemory, setSeatMemory] = useState<string | null>(() => readSeat(sessionId));
  const [actorId, setActorId] = useState<string | null>(
    () => grant?.participant_id ?? seatMemory,
  );
  const grantSeatId = grant?.participant_id ?? null;
  const ownSeatId = ownSeat({ grantSeatId, rememberedSeatId: seatMemory });
  const [lastActor, setLastActor] = useState<string | null>(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [draft, setDraft] = useState<Draft | null>(null);
  /**
   * The same draft, readable synchronously, by whatever writes next.
   *
   * Two handlers can run before React renders between them — the blur the
   * browser fires as it moves focus onto a rating stop, and the stop's own
   * commit, are one gesture — and a write that reads this render's `draft` sends
   * the answer as it was *before* the handler in front of it. Every write path
   * therefore reads the ref, so whichever half of the answer is entered last
   * carries all of it. Mirrored on render as well as in `apply`, so the places
   * that clear the draft (a new seat, a new step, a hand-off) clear this too and
   * a stale answer cannot be written into a cell that has come round again.
   */
  const draftRef = useRef<Draft | null>(null);
  draftRef.current = draft;
  /**
   * The cell whose stored answer the rater has agreed to write over, if any.
   *
   * Only ever one, and only ever the cell in front of them: `forgetDraft` clears
   * it alongside the draft, so a "yes, replace it" given for one seat cannot
   * follow the phone to the next.
   */
  const [replaceOk, setReplaceOk] = useState<string | null>(null);
  /**
   * The cell whose worked-out topic value the rater has picked the control up
   * on, if any. Cleared with the draft, so arming one topic never carries to the
   * next step or to the next person handed the phone.
   */
  const [armed, setArmed] = useState<string | null>(null);
  /**
   * The night's overall, while somebody is overriding it.
   *
   * Held apart from `draft` rather than folded into it, because it is a second
   * answer on the same step and `draft` is one answer per cell. Folding it in
   * would mean a headline and an overall sharing a key, so the tap that set one
   * would be read as a restatement of the other.
   *
   * Null means "whatever the topics say", which is the ordinary case and is not
   * an absence to be filled in: the derived figure is the value until a rater
   * arms the stars and moves them.
   */
  const [overallDraft, setOverallDraft] = useState<{ key: string; value: number } | null>(null);
  const overallDraftRef = useRef<{ key: string; value: number } | null>(null);
  /** Same two-tap gate the topics have, on its own control's key. */
  const [overallArmed, setOverallArmed] = useState<string | null>(null);
  /**
   * Forget the answer in progress — the one place that happens.
   *
   * Every path that changes who is rating calls this, and the list is the point:
   * the seat-change effect below, `pick`, `passOn` and `go`. The moment the phone
   * is passed, the previous rater's half-finished answer must not be readable by
   * the next person, and missing one of those paths puts the leak this file just
   * closed back through a different door.
   *
   * `cellKey` carries `actorId`, so a stale draft is already ignored by
   * everything that reads one — `live`, `apply` and `boundaryWrite` all check the
   * key. This is the other half of the same rule rather than a second store of
   * it: the ref is mirrored on render, and between the handler that changes the
   * seat and that render it would still be holding the answer, readable
   * synchronously by anything that ran in between.
   */
  function forgetDraft(): void {
    draftRef.current = null;
    setDraft(null);
    // The permission to overwrite goes with the answer it was given for. It is
    // one cell's, and the next seat or the next step is a different cell.
    setReplaceOk(null);
    setArmed(null);
    // The overall is the same rater's answer and leaves with the rest of it. Its
    // settle window goes too: a value still waiting in there would land under
    // whichever name the phone was handed to next.
    overallSettler.drop();
    overallDraftRef.current = null;
    setOverallDraft(null);
    setOverallArmed(null);
  }
  const [saving, setSaving] = useState<{ key: string; status: "saving" | "saved" } | null>(null);
  const [unsentList, setUnsentList] = useState<Outgoing[]>([]);
  const [refusedList, setRefusedList] = useState<Outgoing[]>([]);
  const [takingBack, setTakingBack] = useState<string | null>(null);
  const [takeBackError, setTakeBackError] = useState<string | null>(null);
  /**
   * Whether the host has asked about the phone that is not answering.
   *
   * The take-back control is destructive and the state that calls for it — a
   * seat on a phone that has gone — looks exactly like the first ten seconds of
   * every step on a QR night, where nobody has answered yet and nothing is
   * wrong. So it is one quiet tap away rather than on the screen by default:
   * kept for the rest of the night once opened, because a phone that died on
   * step four is still dead on step five.
   */
  const [seatHelp, setSeatHelp] = useState(false);
  const [local, setLocal] = useState<LocalCache>(NO_LOCAL);
  const [localTick, setLocalTick] = useState(0);
  const [sync, setSync] = useState<SyncState>(getSyncState);

  useEffect(() => subscribeSync(setSync), []);

  /**
   * Whether this phone's seat has been taken out from under it.
   *
   * The screen a guest gets when the host takes their seat back, which used to be
   * the wrong one. The read fails, so `state` never arrives, so the branch far
   * below that would have said something useful about a stranded guest is never
   * reached at all — the null-state guard returns first. What they got instead was
   * "Trouble", the server's sentence, and a Try again that was refused for the
   * rest of the night every time it was pressed.
   *
   * Asked with a second request rather than read off the failure in hand, because
   * `useSessionLive` hands this screen `error` as a string and the status is what
   * separates "your seat is gone" from "the wifi is gone". Widening that hook's
   * contract for one screen would put an `ApiError` through the poll loop, the
   * socket path and the results screen to answer a question only this branch asks;
   * one extra request, on a screen that has already failed to load and is showing
   * nothing, is the cheaper half of that trade.
   *
   * It deliberately does *not* also require `state` to be null, which is the
   * narrower guard this was written with and which left the worst case of the
   * three uncovered. A take-back mid-night finds the guest with a table already on
   * screen, and `useSessionLive` keeps the last good read when a poll fails — it
   * sets `error` and leaves `state` alone. So `state` stays non-null forever, the
   * roster it holds is frozen at the moment before the seat was taken, and the
   * seat-eviction effect further down cannot help: it reads that same frozen
   * roster, and even given a fresh one `seatTakenElsewhere` returns false for both
   * revocation paths — `takeBackSeat` nulls `claimed_at`, which it treats as "not
   * claimed", and `reclaimAnswerlessSeat` leaves the seat's id equal to this
   * device's own. The guest was left looking at a stale table under "Live updates
   * are down", tapping gems into refusals, with nothing on screen ever saying why.
   * Firing on `error` alone costs one request per distinct failure — a verdict of
   * `unknown` is what a flaky connection gets, and it changes nothing.
   *
   * Nothing is cleared here, and that is deliberate. The dead token is what makes
   * `/session/:id` a public path for a phone with no account (`isPublicPath`), so
   * dropping it would trade this sentence for the front door the next time the
   * guest reloads — a sign-in page, at a game table, for somebody who has no
   * account and wants none. The token is thrown away one screen later, by
   * `classifyJoin`, in the same breath as being offered a seat.
   *
   * Declared above the read it is about so it can switch the read off. A verdict
   * of dead is the one thing this screen can learn that makes another attempt
   * pointless: the poll is one request per four seconds, against a token the
   * server has already decided about, and it ran all night on the phone this was
   * reported from. Every other failure keeps polling, because every other failure
   * is a "not now" that the next attempt may well get past.
   */
  const [seatGone, setSeatGone] = useState(false);

  // The server resolves a bearer grant before it ever looks at `?as=`, so a
  // guest sends none: the token already names the only seat it may see.
  const { state, error, loading, channel, cancelled, refresh } = useSessionLive(sessionId, {
    as: role === "guest" ? null : actorId,
    token,
    enabled: sessionId !== "" && !seatGone,
  });

  useEffect(() => {
    if (!grant || loading || error === null) return;
    let alive = true;
    void grantVerdict(grant).then((verdict) => {
      if (alive && verdict === "dead") setSeatGone(true);
    });
    return () => {
      alive = false;
    };
  }, [grant, state, loading, error]);

  // Replace rather than push: Back off the results screen is how people get
  // here, and pushing would put the results in front of this screen again, so
  // the next Back would land here and bounce a second time. Replacing spends the
  // history entry this screen was going to occupy anyway.
  const finishedTo = finishedNightRoute(sessionId, state?.session.status ?? null);
  useEffect(() => {
    if (finishedTo !== null) navigate(finishedTo, { replace: true });
  }, [finishedTo]);

  /**
   * The topics whose follow-ups are on the loop, and why it is one set and not
   * one per seat.
   *
   * On a shared device the step list is the table's, not a rater's — the phone
   * goes round on the step everyone is on — so "let's go into detail on
   * Mechanics" is a decision the table takes together, which is also how it gets
   * taken at a real table. On a QR night every phone runs this screen with its
   * own copy of this set, so the decision is each person's, without a line of
   * code knowing the difference.
   *
   * It only ever grows. Removing an id would shorten the step list *behind* the
   * index somebody is standing on and move them to a different question without
   * a gesture, which is the one navigation bug a rating loop cannot have.
   */
  const [detailed, setDetailed] = useState<ReadonlySet<string>>(() => new Set<string>());

  const steps = useMemo(() => (state ? buildSteps(state, detailed) : []), [state, detailed]);
  const followUps = useMemo(
    () => (state ? followUpsByAspect(state) : new Map<string, SubQuestionView[]>()),
    [state],
  );
  const index = steps.length === 0 ? 0 : Math.min(stepIndex, steps.length - 1);

  /**
   * Follow-ups this seat has already answered re-open their topic.
   *
   * Otherwise a reload mid-night — or a phone that comes back after the queue
   * drained — leaves six committed answers behind a branch the loop no longer
   * offers, with nothing on screen saying they exist. Read off who committed,
   * which is public (it is what the "waiting on" line is made of), and never off
   * a value: this must not become a second route to somebody's answer.
   *
   * ## Why it also moves the index, and why it runs down here
   *
   * The set only ever grows, and growing it was assumed to be safe because the
   * comment above `detailed` reasons about *shortening* the list behind somebody.
   * Growing it above them does exactly the same damage: the step you are standing
   * on is a position in a list, so inserting six questions before it hands you a
   * different question without a gesture. Claiming a seat is when this fires —
   * "I'm olli — my turn" on step 2 of 7 (Regelwerk) landed on step 2 of 13, the
   * first follow-up under Material, and the next rating typed in overwrote an
   * answer to a question nobody had opened. A silent jump that then eats input is
   * the worst shape this loop has; found by walking the night, invisible to the
   * suite.
   *
   * So count what is being inserted above the standing step and shift by it,
   * which needs `steps` and `index` — hence the effect sits below them rather
   * than beside the state it sets. Blocks that open *after* where you stand cost
   * nothing: their questions appear ahead of you, which is where they belong.
   */
  useEffect(() => {
    if (!state || !actorId) return;
    const opening: string[] = [];
    for (const [aspectId, subs] of followUps) {
      if (detailed.has(aspectId)) continue;
      if (subs.some((s) => s.ratings.some((r) => r.participant_id === actorId))) {
        opening.push(aspectId);
      }
    }
    if (opening.length === 0) return;

    const inserted = stepShiftOnOpen(steps, index, opening, followUps);

    setDetailed((prev) => {
      const next = new Set(prev);
      for (const id of opening) next.add(id);
      return next;
    });
    if (inserted > 0) setStepIndex((at) => at + inserted);
  }, [state, actorId, detailed, steps, index, followUps]);
  const rawStep = steps[index];
  const mode: CaptureMode = state?.session.capture_mode ?? "SHARED_DEVICE";
  // A guest is on their own phone whatever the session says, so the shared-device
  // phrasing ("pass the phone") would be an instruction to nobody.
  const phrasing: CaptureMode = role === "guest" ? "MULTI_DEVICE" : mode;
  const writePath = writePathFor(mode, role);
  const outbox = writePath === "outbox";

  // Whose row this screen may unredact, which on a shared device is nobody's —
  // not even the seat being rated right now. `ownAnswerReadable` carries the
  // reasoning; `withLocal` is given the answer rather than `actorId`, because
  // `actorId` on a shared device is only the last name tapped.
  const readable = ownAnswerReadable(mode, role);

  /**
   * The night's overall, on the one step that asks for it.
   *
   * Gated on `readable` for exactly the reason the topic figure is: a mean of six
   * topics is those topics, so on a phone going round the table it is shown to
   * nobody. Null therefore means two different things here — this step is not the
   * headline, or this night may not read it back — and both of them mean the same
   * thing to the markup, which is why they are one expression rather than two.
   *
   * Memoised because it walks every topic and every follow-up this seat
   * answered, and this component re-renders on every frame of a star drag —
   * including drags of the star row right above it, which cannot change it.
   *
   * ## Why it is computed up here and not beside the markup that reads it
   *
   * It is a hook, and every early return further down this component is below
   * it. A screen that has just been opened renders once with no `state` and
   * takes the `!state` guard; the read then lands, the guard is not taken, and a
   * hook that lives past it runs on that render and not on the one before —
   * which React ends with `Rendered more hooks than during the previous render`
   * and a blank screen. Opening a fresh night is the ordinary way to hit that,
   * because a night this device has never read has nothing to paint on the first
   * render. So this reads `rawStep` rather than `step`: `withLocal` only folds
   * local rows into the view and never touches `kind`, and the value has to be
   * computed where every render reaches it.
   */
  const overallDerived: OverallValue | null = useMemo(
    () =>
      state && rawStep?.kind === "headline" && readable && actorId
        ? overallValueOf(state, actorId, followUps)
        : null,
    [rawStep?.kind, readable, actorId, state, followUps],
  );

  // Writes are chained rather than fired in parallel: the outbox path reads a
  // row's version before writing it, and two overlapping commits to the same
  // cell would both read the same base version.
  const queue = useRef<Promise<void>>(Promise.resolve());
  const unsent = useRef(new Map<string, Outgoing>());
  /**
   * Writes the server has refused outright — a separate list on purpose.
   *
   * Everything the app does about an unsent rating is wrong for a refused one:
   * it is not retried, it is not counted as pending, it does not hold the page,
   * and it is not something the table is waiting on. Folding the two together
   * is precisely the bug — the same 409 came back five times under a banner
   * saying "Retrying on its own", and leaving then asked whether to risk losing
   * a number the server had already declined.
   */
  const refused = useRef(new Map<string, Outgoing>());
  const inflight = useRef(new Map<string, number>());
  /**
   * The last answer this screen wrote for each cell, and when.
   *
   * Not a cache and not a record of success — purely "this has been said once
   * already", so that two boundaries crossed in quick succession (the note
   * field's own, then Next a moment later) do not send one answer twice. The
   * second of those writes was the more dangerous one: it can be refused
   * *because the first one landed and revealed the cell*, and the screen would
   * then tell the rater their rating had not been saved while it sat on the
   * table in front of them.
   *
   * The entry goes the moment the table turns the write down. "Said once" is only
   * a reason not to say it again while it still stands — a refused answer does
   * not stand, and a rater who enters it a second time (the seat handed back, the
   * connection returned) has to be able to get it onto the table.
   *
   * The timestamp is what tells a refusal read back out of the outbox whether it
   * is about the answer in front of the rater or about a superseded attempt.
   */
  const sentAnswers = useRef(new Map<string, { answer: Draft; at: number }>());
  const landed = useRef(false);

  // One per mounted screen, and stable across renders: the value waiting in it
  // has to survive the re-render its own draft causes.
  const settlerBox = useRef<Settler | null>(null);
  const settler = (settlerBox.current ??= createSettler(SETTLE_MS, browserClock));
  /**
   * A second settler, for the second control on the headline step.
   *
   * Not the one above, and this is not tidiness. A settler holds exactly one
   * pending write and `offer` discards whatever was waiting — so a rater who set
   * the headline and reached straight for the overall would have had the headline
   * silently dropped, on the one rating nobody may skip. Two controls that can be
   * touched without a boundary in between need two windows.
   */
  const overallSettlerBox = useRef<Settler | null>(null);
  const overallSettler = (overallSettlerBox.current ??= createSettler(SETTLE_MS, browserClock));

  // Held in a ref because `push` is declared past the early returns below and
  // closes over this render's values, while the subscription must be set up
  // once, before them.
  const retryUnsent = useRef<() => void>(() => {});
  /**
   * Same reason: `commit` is declared past the early returns, and the two
   * boundaries nobody chooses — the tab going away, this screen unmounting —
   * have to be subscribed to before them.
   *
   * Emptied on the way past, every render, and filled in again once `commit`
   * exists. A render that bails out below has no cell, no seat and no step to
   * write, and the render before it left a closure over the one it had: a night
   * another device has just finished returns at `finishedTo`, redirects, unmounts
   * — and this ref would have written a rating into a session that is over.
   */
  const commitBoundary = useRef<(boundary: Boundary) => void>(() => {});
  commitBoundary.current = () => {};

  /**
   * Ride the store's recovery schedule instead of waiting for a tap.
   *
   * This queue used to retry on exactly one event: somebody noticing the "Not
   * sent" banner and pressing Try again. A rating refused by the server while
   * the phone was online therefore sat here forever — the table waited on a
   * reveal that could not come, and the screen blamed the other players for it.
   * `subscribeRecovery` fires on every moment the outbox itself retries, so
   * there is one retry clock in the app rather than two that drift apart.
   */
  useEffect(() => subscribeRecovery(() => retryUnsent.current()), []);

  /**
   * The last boundary there is: this screen going away.
   *
   * A settled value waits up to `SETTLE_MS` on the device and nowhere else — it
   * is not in `unsentKeys`, so the `beforeunload` guard in `api.ts` knows
   * nothing about it, and on the HTTP path there is no outbox holding a copy.
   * Every boundary a person crosses on purpose already forces it out through
   * `commit("leave")`; this is the two that happen to them. `pagehide` is the tab
   * away or iOS backgrounding the browser, and the cleanup is hash navigation,
   * which unmounts this screen without firing any event at all — the same hole
   * the unsent history guard below exists for.
   *
   * Both halves of the answer leave, in that order. `commit("leave")` writes the
   * draft as it stands — including a note nobody ever put a number next to,
   * which at this boundary is the whole of what the rater had to say and is lost
   * for good if it is not written now — and `settler.flush()` then sends any
   * value still inside the settle window, which after a commit for this cell can
   * only belong to a different one.
   */
  useEffect(() => {
    const out = (): void => {
      commitBoundary.current("leave");
      settler.flush();
    };
    if (typeof window === "undefined") return out;
    window.addEventListener("pagehide", out);
    return () => {
      window.removeEventListener("pagehide", out);
      out();
    };
  }, [settler]);

  /**
   * The Back button, on a router made of hashes.
   *
   * `beforeunload` is the only warning the HTTP write path can honestly give:
   * that device has no outbox, so a rating it could not send lives in this
   * component and nowhere else. But hash navigation never leaves the document,
   * so Back unmounts this screen without firing that event at all — the number
   * goes, in silence, and the next screen has no idea it happened. One of them
   * even offers "Nothing has been lost" while it is being said.
   *
   * So while anything is unsent a duplicate history entry is held in front of
   * this screen. Back pops that one instead: same URL, so the router does not
   * move and nothing unmounts, and the question is asked while the rating is
   * still here to keep. Staying puts the entry back; leaving steps past both, to
   * wherever Back was going in the first place.
   */
  const holdingUnsent = unsentList.length > 0;
  useEffect(() => {
    if (!holdingUnsent) return;
    if (typeof window === "undefined" || typeof window.history?.pushState !== "function") return;

    // The marker, not a counter: StrictMode's second pass and a re-render must
    // not each stack another entry the reader then has to press Back through.
    const isArmed = (): boolean =>
      (window.history.state as { pbUnsent?: boolean } | null)?.pbUnsent === true;
    const arm = (): void => {
      if (!isArmed()) window.history.pushState({ pbUnsent: true }, "", window.location.href);
    };

    let live = true;
    const onPop = (): void => {
      if (!live) return;
      // No `confirm` is a test harness or an embedded view, not a licence to
      // trap somebody on a screen they asked to leave.
      const leaving = typeof window.confirm === "function" ? window.confirm(LEAVE_UNSENT) : true;
      if (leaving) {
        live = false;
        window.history.back();
        return;
      }
      arm();
    };

    arm();
    window.addEventListener("popstate", onPop);
    return () => {
      live = false;
      window.removeEventListener("popstate", onPop);
    };
  }, [holdingUnsent]);

  // A seat that is not at this table any more is not a seat — and neither is one
  // another phone has claimed since this device picked it, which is the same
  // wrong answer arriving in the other order: the picker refuses a claimed seat,
  // so a seat claimed *after* the pick has to be given back the same way. Re-ask
  // rather than keep publishing under a name that is now answering elsewhere.
  useEffect(() => {
    if (!state || !actorId) return;
    const seat = state.participants.find((p) => p.id === actorId);
    if (!seat || seatTakenElsewhere({ phrasing, role, grantSeatId, ownSeatId, seat })) {
      setActorId(null);
      // The seat is changing hands whether anybody chose it or not, so the answer
      // in progress goes with it. Nothing is lost that could have been kept: an
      // answer under a name this device may no longer publish is refused by the
      // server anyway.
      forgetDraft();
    }
  }, [state, actorId, phrasing, role, grantSeatId, ownSeatId]);

  // On a phone of one's own, open on the first thing this person has not
  // answered. Never on a shared device: there the table moves through the
  // categories together, and jumping to one person's gap breaks that.
  useEffect(() => {
    if (landed.current || !state || !actorId || steps.length === 0) return;
    if (mode === "SHARED_DEVICE" && role === "owner") return;
    landed.current = true;
    const first = steps.findIndex((s) => !answeredStep(s, actorId, followUps));
    setStepIndex(first < 0 ? steps.length - 1 : first);
  }, [state, actorId, steps, mode, role, followUps]);

  const stepKey = rawStep?.key ?? "";
  const entity: Entity | null = rawStep
    ? rawStep.kind === "headline"
      ? "headline_rating"
      : rawStep.kind === "subquestion"
        ? "subquestion_rating"
        : "aspect_rating"
    : null;
  const rowTarget = rawStep
    ? rawStep.kind === "headline"
      ? sessionId
      : rawStep.kind === "subquestion"
        ? rawStep.view.subquestion.id
        : rawStep.view.aspect.id
    : "";
  // A stable dependency: the participant list object is new on every poll, but
  // the seats at the table are not.
  const seatKey = state ? state.participants.map((p) => p.id).join(",") : "";

  useEffect(() => {
    if (!outbox || !entity || stepKey === "" || seatKey === "") {
      setLocal(NO_LOCAL);
      return;
    }
    let alive = true;
    const ids = seatKey.split(",");
    void Promise.all(
      ids.map(async (id) => {
        const row = await readLocal(entity, ratingRowId(rowTarget, id));
        const parsed = row ? toLocalRow(row) : null;
        return parsed ? ([id, parsed] as const) : null;
      }),
    )
      .then((entries) => {
        if (!alive) return;
        setLocal({ key: stepKey, rows: new Map(entries.flatMap((e) => (e ? [e] : []))) });
      })
      .catch(() => {
        if (alive) setLocal(NO_LOCAL);
      });
    return () => {
      alive = false;
    };
    // The sidelined counts are in here because a refused push is followed by
    // `restoreFromServer`, which rewrites this device's row to whatever the
    // server really holds. Without them the screen would go on showing the row
    // the outbox has already given up on.
  }, [outbox, entity, rowTarget, stepKey, seatKey, localTick, sync.conflicts, sync.deadLetters]);

  /**
   * The refusals that happen off-screen, brought back on.
   *
   * On the shared-device path a write is a row in IndexedDB and a push that
   * happens later, so the server's "no" arrives long after the rater has been
   * told the answer is saved on this phone. `outboxRefusal` reads the sidelined
   * ops for exactly this cell and this seat, and only ones set aside since this
   * screen last wrote it; the match is fed into the same `refused` list a live
   * refusal goes into, so there is one "Not saved" notice on this screen rather
   * than two mechanisms saying similar things differently.
   *
   * The other half is as important and is easier to get wrong: taking the notice
   * back down. A dead letter is kept until somebody clears it from the sync
   * screen, so a notice pinned to one that has been resolved — or superseded by a
   * write that did land — would sit on the rating screen for the rest of the
   * night with no control anywhere near it. So every notice this effect owns is
   * re-checked on every run, including ones for steps and seats the table has
   * since moved past, and dropped as soon as `outboxNoticeStale` says so.
   *
   * Notices from a refusal the rater saw in front of them are never touched here:
   * they belong to `push`, and clearing one on the strength of an unrelated
   * outbox change would take a true notice off the screen.
   */
  const stepTitle = rawStep?.title ?? "";
  useEffect(() => {
    if (!outbox) return;
    let alive = true;
    // A seat is needed to name a row, and there is none on the seat picker. The
    // reconciliation below still runs: a notice already on screen has to be able
    // to go while the phone is between hands.
    const cell = actorId === null || stepKey === "" ? null : `${stepKey}|${actorId}`;
    void Promise.all([conflicts(), deadLetters()])
      .then(([held, dead]) => {
        if (!alive) return;
        const all = [...held, ...dead];
        const stillSidelined = new Set(all.map((s) => s.op_id));
        let changed = false;

        for (const [key, out] of [...refused.current]) {
          if (out.outboxOp === undefined) continue;
          const lastWrite = sentAnswers.current.get(key)?.at ?? 0;
          if (!outboxNoticeStale(out.outboxOp, stillSidelined, lastWrite)) continue;
          refused.current.delete(key);
          changed = true;
        }

        const written = cell === null ? undefined : sentAnswers.current.get(cell);
        const found =
          cell !== null && entity !== null && actorId !== null && written !== undefined
            ? outboxRefusal(entity, ratingRowId(rowTarget, actorId), all, written.at)
            : null;
        if (found !== null && cell !== null && actorId !== null && entity !== null) {
          const target =
            entity === "subquestion_rating"
              ? subQuestionTarget(rowTarget, found.answer)
              : entity === "aspect_rating"
                ? aspectTarget(rowTarget, found.answer)
                : found.answer.value === null
                  ? null
                  : ({ kind: "headline", value: found.answer.value } as const);
          // No target means a headline row with no number in it, which no write
          // path can produce. Nothing to say about a row that cannot exist.
          const existing = refused.current.get(cell);
          if (target !== null && existing?.outboxOp?.opId !== found.opId) {
            refused.current.set(cell, {
              key: cell,
              participantId: actorId,
              label: stepTitle,
              target,
              message: found.message,
              outboxOp: { opId: found.opId, at: found.at },
            });
            changed = true;
          }
        }

        if (changed) setRefusedList([...refused.current.values()]);
      })
      .catch(() => {
        // A store that cannot be read is not evidence of a refusal, and this
        // screen has a louder failure for a store that is broken.
      });
    return () => {
      alive = false;
    };
  }, [
    outbox,
    entity,
    rowTarget,
    stepKey,
    stepTitle,
    actorId,
    sync.conflicts,
    sync.deadLetters,
    // The counts above move when an op is set aside; this moves when a write of
    // this screen's own finishes, which is the other thing that can make a
    // notice stale.
    localTick,
  ]);

  if (sessionId === "") {
    return (
      <div className="pb-empty">
        <div className="pb-label">Session</div>
        <p>No session in that link.</p>
      </div>
    );
  }

  // Before `seatGone`, and before the null-state guard, for two different
  // reasons that both end here.
  //
  // Before `seatGone` because deleting a night revokes its grants, so a guest
  // phone can reach both verdicts at once — and "scan the QR code again to
  // rejoin" would be sending somebody back to a table that no longer exists.
  // Before the null-state guard because `state` is still holding the last good
  // read: the screen is fully painted, so an arm inside that guard would never
  // be reached and the guest would go on tapping gems into a night that is gone.
  if (cancelled) {
    return (
      <div className="pb-empty">
        <div className="pb-label">This night is gone</div>
        <p>
          Whoever set this game up deleted the night while it was open. Every rating on it went
          with it — nothing was kept, and there is nothing left here to see or finish.
        </p>
        {role === "owner" ? (
          <button
            type="button"
            className="pb-btn"
            data-variant="primary"
            onClick={() => navigate("/", { replace: true })}
          >
            Back to the shelf
          </button>
        ) : (
          // No button: this phone has no account, so every route in this app
          // leads it to a sign-in page it has no business seeing.
          <p>Nothing more to do on this phone — you can hand it back.</p>
        )}
      </div>
    );
  }

  // A seat that is gone is not a load that failed, and must not be dressed as
  // one. No Try again: that button was pressed all evening on production and was
  // refused every time, because the request it repeats is the request the server
  // has already decided about. What replaces it is the only thing that does work
  // — and does work now, which is the other half of this fix — and it is a
  // sentence rather than a button because the QR code is on somebody else's
  // screen and no route in this app can reach it. A button that dropped the grant
  // to go somewhere would land on the front door instead; see the effect above.
  //
  // Above the null-state guard, not inside it, and that placement is the whole
  // point rather than tidiness. A take-back mid-night leaves the last good read
  // sitting in `state`, so the guard below does not fire and an arm inside it
  // would never be reached — the guest would keep the frozen table, which is the
  // exact failure the effect above was widened to catch. A condemned grant means
  // nothing on this screen is true any more, whether or not something is already
  // painted, so it answers before anything that reads `state` gets a turn.
  if (seatGone) {
    return (
      <div className="pb-empty">
        <div className="pb-label">Not at this table</div>
        <p>
          This phone&rsquo;s place at the table has been given back, so it cannot see the ratings any
          more. Scan the QR code again to pick a name and rejoin — whoever set the game up can show
          it.
        </p>
      </div>
    );
  }

  if (!state) {
    return (
      <div className="pb-empty">
        <div className="pb-label">{loading ? "Table" : "Trouble"}</div>
        <p>{loading ? "Getting the table…" : (error ?? "Could not load this session.")}</p>
        {loading ? null : (
          <button type="button" className="pb-btn" onClick={() => void refresh()}>
            Try again
          </button>
        )}
      </div>
    );
  }

  // The redirect above is an effect, so it lands a frame late. Nothing
  // interactive may paint in that frame: a gem tapped here writes a rating the
  // server has already stopped accepting.
  if (finishedTo !== null) {
    return (
      <div className="pb-empty">
        <div className="pb-label">That night is done</div>
        <p>Nothing more goes on this one. Here is what the table said.</p>
        <button
          type="button"
          className="pb-btn"
          data-variant="primary"
          onClick={() => navigate(finishedTo, { replace: true })}
        >
          See the results
        </button>
      </div>
    );
  }

  const participants = state.participants;

  if (participants.length === 0) {
    return (
      <div className="pb-empty">
        <div className="pb-label">Nobody at the table</div>
        <p>This session has no players yet.</p>
      </div>
    );
  }

  if (!rawStep) {
    return (
      <div className="pb-empty">
        <div className="pb-label">Nothing to rate</div>
        <p>This session has no questions on it.</p>
      </div>
    );
  }

  const localRows = local.key === rawStep.key ? local.rows : new Map<string, LocalRow>();
  const step = withLocal(rawStep, localRows, readable ? actorId : null);

  const actor = participants.find((p) => p.id === actorId) ?? null;
  const cellKey = `${step.key}|${actorId ?? ""}`;

  // Two sources, both first-hand: a row this device wrote, and the server's
  // redacted answer — the latter only when the server agrees about who is
  // acting, so a half-applied hand-off can never show the previous rater. Read
  // off `rawStep`, before the local rows were folded in, so each source is
  // handed to `ownAnswer` once and under its own name.
  const own = ownAnswer({
    readable,
    local: actorId ? (localRows.get(actorId) ?? null) : null,
    server: actorId && state.actor_participant_id === actorId ? ownCell(rawStep, actorId) : null,
  });

  // The answer in front of the rater. A draft for this cell is what this person
  // is making right now and is theirs to see — dragging a gem you cannot see is
  // not a thing anybody can do. Anything else falls back to what the table holds,
  // which on a shared device is nothing this screen is allowed to know: an answer
  // committed and put away is gone until the reveal.
  const live: Draft =
    draft && draft.key === cellKey
      ? draft
      : {
          key: cellKey,
          value: own.shown?.value ?? null,
          na: own.shown?.na ?? false,
          note: own.shown?.note ?? "",
        };

  // This seat has answered and this screen may not say what it answered — so
  // every control here is sitting on top of a stored row, and a change to one of
  // them is a replacement rather than a first answer.
  const sealed = sealedCell(own);
  // Asked of three different drafts — this render's, the one a tap is producing,
  // the one a boundary is committing — and the answer has to be the same rule
  // every time, so the rule is bound once here rather than restated at each site.
  const heldFor = (candidate: Draft | null): boolean =>
    replacementHeld({ sealed, confirmed: replaceOk === cellKey, draft: candidate, cellKey });
  const held = heldFor(draft);
  // The row under a sealed cell, for `replacementRow` to merge from and for
  // nothing else. `own.shown` is the display question and it has already been
  // answered with null; this is the write question, and the row is sitting in
  // this device's own replica either way.
  const stored = sealed && actorId ? (localRows.get(actorId) ?? null) : null;

  const followUpsHere = step.kind === "aspect" ? (followUps.get(step.key) ?? []) : [];
  const progress = progressOfStep(step, participants, followUpsHere);
  const cells =
    step.kind === "aspect"
      ? topicCellsOf(step.view, followUpsHere, participants)
      : cellsOf(step, participants);
  // Not `topicCells`, which reads the view again: before the reveal that would
  // derive this seat's own figure out of their visible follow-up rows and put it
  // on the shared strip, where the pass-the-phone night shows nobody a number.
  // The cells above have already been through `topicCellsOf`'s reveal gate.
  const topicRatings: RatingCell[] | undefined =
    step.kind === "aspect" ? visibleRatingCells(cells) : undefined;
  const stops = stopsOf(step);
  const isLast = index === steps.length - 1;

  /**
   * How many of this topic's follow-ups this seat has committed.
   *
   * Committed, not answered-with-a-number: the count is public — it is the same
   * fact the "waiting on" line is built from — and it is asked here to decide
   * whether this seat still owes the topic an answer. A skip written as n/a
   * counts, exactly as it does at the reveal gate, so the two cannot disagree
   * about whether somebody is holding the table up.
   */
  const detailCommitted = actorId
    ? followUpsHere.filter((s) => s.ratings.some((r) => r.participant_id === actorId)).length
    : 0;

  /**
   * The topic value this seat's follow-ups work out to, or null.
   *
   * Gated on `readable` for the same reason `ownAnswer` is: on a shared device
   * this screen may not read a committed answer back, and a mean of six answers
   * is still those answers — one subtraction away, and on a two-question topic
   * not even that. So the phone-round-the-table night shows the derived figure
   * to nobody, and shows the provenance line instead, which says a seat answered
   * without saying what.
   */
  const derived: TopicValue | null =
    step.kind === "aspect" && readable && actorId
      ? topicValueOf(step.view, followUpsHere, actorId)
      : null;
  /**
   * Showing a worked-out number rather than one this rater typed.
   *
   * Dropped the moment there is a live answer in front of them: a draft is what
   * they are saying now, and a control that keeps displaying the old figure
   * under a finger that is moving is the one thing a rating control may not do.
   */
  const derivedShown: TopicValue | null =
    derived && derived.source === "derived" && derived.value !== null && live.value === null && !live.na
      ? derived
      : null;
  const armedHere = armed === cellKey;
  const lockedStars = derivedShown !== null && !armedHere;

  // The cell the night's overall is written to. `overallDerived` — the figure
  // the topics work out to, and the reasoning for it — is computed above every
  // early return, because it is a hook.
  const overallKey = `overall|${actorId ?? ""}`;
  const overallLive =
    overallDraft && overallDraft.key === overallKey ? overallDraft.value : null;
  const overallArmedHere = overallArmed === overallKey;
  /**
   * Showing a figure worked out from the topics rather than one anybody chose.
   *
   * Dropped as soon as this rater has put a number of their own in front of them,
   * and while the control is armed — an armed control still displaying the mean
   * would refuse the tap that lands on it (`planCommit` calls a restatement no
   * change), so "yes, that one" would be the one gesture that wrote nothing.
   */
  const overallDerivedShown: OverallValue | null =
    overallDerived &&
    overallDerived.source === "derived" &&
    overallDerived.value !== null &&
    overallLive === null
      ? overallDerived
      : null;
  const overallLocked = overallDerivedShown !== null && !overallArmedHere;
  const overallShown =
    overallLive !== null
      ? overallLive
      : overallArmedHere
        ? null
        : (overallDerived?.value ?? null);

  const stuckHere = deliveryStalled({
    unsent: unsentList.length,
    outbox,
    status: sync.status,
    pending: sync.pending,
  });

  function publish(): void {
    setUnsentList([...unsent.current.values()]);
    setRefusedList([...refused.current.values()]);
  }

  function push(out: Outgoing): void {
    // A fresh attempt clears whatever the last one was refused for: the notice
    // describes the write in front of it, not a write that has been superseded.
    // True of a refusal read back out of the outbox as well — it is about an
    // older attempt at this same cell, and `outboxNoticeStale` will not let it
    // come back while this write is the newest one.
    refused.current.delete(out.key);
    inflight.current.set(out.key, (inflight.current.get(out.key) ?? 0) + 1);
    // Deliberately not cleared here. A retry in flight is not a rating that
    // landed, and dropping it from the list for the second or so the request
    // takes flipped the screen back to "waiting for the table to catch up" —
    // blaming the room for a write that is still stuck on this phone. The entry
    // goes when the server takes it, and not a frame before.
    publish();
    setSaving({ key: out.key, status: "saving" });

    const run = async (): Promise<void> => {
      try {
        await commitRating({
          sessionId,
          participantId: out.participantId,
          mode,
          role,
          token,
          target: out.target,
        });
        unsent.current.delete(out.key);
      } catch (err) {
        if (err instanceof RefusedRating) {
          // Named, but not owed. `commitRating` has already taken it off the
          // transport's books; this takes it off the screen's, so the guard
          // below stops calling it pending and nothing here retries it.
          unsent.current.delete(out.key);
          refused.current.set(out.key, { ...out, message: refusedDetail(err) });
          // A refused answer has not been said, whatever this screen thought
          // when it said it. Left in place, the rater who enters the same answer
          // again — the seat handed back, the night reopened — would have it
          // quietly swallowed as a repeat, and the table would wait on them.
          sentAnswers.current.delete(out.key);
          // And the number in the input is not what the table has. Dropping the
          // draft falls the cell back to the server's own answer — otherwise
          // this phone goes on showing a value nobody else's does, which is the
          // same divergence the outbox path rolls back in `store/sync.ts`.
          setDraft((current) => (current && current.key === out.key ? null : current));
          // The permission to write over a sealed answer goes with it. It was
          // granted by a write that has since been refused, so the stored answer
          // is still whatever it was — and leaving the permission behind would
          // let the next tap on this cell overwrite it with no question asked,
          // which is the whole of the bug this gate exists to stop.
          setReplaceOk((current) => (current === out.key ? null : current));
          // A value still waiting in the settle window would be sent straight
          // into the door that just closed, and refused in the same words. The
          // refusal is about the cell, not about the number in it — and only
          // about *that* cell, so a late answer here cannot silently throw away
          // a rating the person has since started on the next step.
          settler.drop(out.key);
        } else {
          // Kept, named and retryable. A rating that failed to send is not
          // allowed to disappear because the table moved on to the next
          // category.
          unsent.current.set(out.key, { ...out, message: unsentDetail(err) });
        }
      }

      const left = (inflight.current.get(out.key) ?? 1) - 1;
      if (left <= 0) inflight.current.delete(out.key);
      else inflight.current.set(out.key, left);

      publish();
      // Only the last write of a burst gets to say "saved" — otherwise a drag
      // across the track reports success while a newer value is still queued.
      if (left <= 0) {
        const failed = unsent.current.has(out.key) || refused.current.has(out.key);
        setSaving(failed ? null : { key: out.key, status: "saved" });
      }
      if (outbox) setLocalTick((t) => t + 1);
      // The write may have been the one that closed the reveal gate, but this
      // device does not get to decide that — re-read and let the server say.
      void refresh();
    };

    queue.current = queue.current.then(run, run);
  }

  retryUnsent.current = () => {
    for (const out of [...unsent.current.values()]) push(out);
  };

  /**
   * A rating leaving this screen for good. The one place that happens.
   *
   * Anything still inside the settle window is this same cell's older value, and
   * whatever is calling here is newer, so the waiting write is dropped rather
   * than allowed to land behind the one replacing it.
   */
  function send(next: Draft, at: Step, participantId: string): void {
    settler.drop(next.key);
    // What goes to the table is the rater's answer plus whatever half of the
    // stored one they were never shown — see `replacementRow`. On every other
    // kind of cell `stored` is null and this is the draft, untouched.
    const target = targetFor(at, replacementRow(next, stored));
    if (!target) return;
    // Recorded before the write goes, not after it comes back: the question this
    // answers is "has this already been said", and it has been.
    sentAnswers.current.set(next.key, { answer: next, at: Date.now() });
    // Whoever is in this seat has now written this cell themselves, so the cell
    // stops being somebody else's sealed answer and starts being theirs. Without
    // this the seal closes behind the rater's own first write — the row lands in
    // the replica a moment later, the value is not readable back on a shared
    // device, and the screen would turn round and ask them whether to replace the
    // answer they are still in the middle of giving. The permission is dropped
    // with the draft, so passing the phone on puts the question back.
    setReplaceOk(next.key);
    push({ key: next.key, participantId, label: at.title, target });
  }

  /**
   * A change to the answer in front of the rater.
   *
   * The draft moves this frame, always: what the finger feels is not allowed to
   * depend on the network, and the gem, the readout and the waiting list all
   * read from the draft. What waits is the *write*, and only until the value has
   * stood still — see `createSettler`. A drag across nine stops is nine drafts
   * and one write, of the ninth.
   */
  function apply(patch: Partial<Omit<Draft, "key">>): void {
    // Built on the newest draft rather than on this render's, because two
    // changes to one answer can arrive before React renders between them. The
    // gesture that made this necessary is a tap on a rating stop while the note
    // field has focus: the browser blurs the textarea and the stop commits
    // inside the same dispatch, and a patch built on the pre-blur render would
    // drop whichever half of the answer was entered first.
    const base = draftRef.current?.key === cellKey ? draftRef.current : live;
    const next: Draft = { ...base, ...patch, key: cellKey };
    // The gem fires on both the rail tap and the native input event, and the
    // same value twice is not a change worth a round trip.
    if (next.value === base.value && next.na === base.na && next.note === base.note) return;
    draftRef.current = next;
    setDraft(next);
    // Whether this change opens a settle window is a question about the answer,
    // not about which control raised it: a whole answer — a number, or the
    // deliberate "doesn't apply" — is on its way to the table, and a half-built
    // one is not going anywhere until the rater leaves. Which is also why a note
    // typed next to a number re-opens the window on every keystroke: the words
    // are part of that answer, and the alternative is what a table actually saw —
    // the window closing mid-sentence, writing "it dr", and the reveal firing on
    // it, after which the field is locked and the rest of the sentence is refused.
    // A replacement nobody has agreed to yet opens no window. Asked of `next`
    // rather than of the render's `held`, for the same reason the patch is built
    // on `draftRef`: the first change to a sealed cell and the render that puts
    // the question on screen are not the same frame, and the window must not
    // open in between.
    if (!heldFor(next) && (next.value !== null || next.na) && actorId) {
      const seat = actorId;
      settler.offer(cellKey, () => {
        // Read when the window closes, not when it was opened, so the write
        // carries the whole answer as it stands at the moment it leaves.
        const now = draftRef.current;
        if (!now || now.key !== cellKey) return;
        send(now, step, seat);
      });
    }
  }

  /**
   * The rater moving the night's overall off what its topics work out to.
   *
   * Its own small write path rather than a branch inside `apply`, because it is
   * not a draft in that sense: there is no note, no n/a, no sealed-replacement
   * question — an override is a number or it is nothing. What it does share is
   * the settle window, and for the same reason: a sweep across the row emits
   * every star it crosses and only the last of them is the answer.
   */
  function applyOverall(value: number): void {
    if (!actorId) return;
    const seat = actorId;
    const key = overallKey;
    if (overallDraftRef.current?.key === key && overallDraftRef.current.value === value) return;
    overallDraftRef.current = { key, value };
    setOverallDraft({ key, value });
    overallSettler.offer(key, () => {
      // Read when the window closes, not when it opened: the write carries the
      // star the finger came to rest on.
      const now = overallDraftRef.current;
      if (!now || now.key !== key) return;
      // Recorded the way `send` records its own, and for the one thing this key
      // is looked up for: `outboxNoticeStale` asks when this row was last
      // written, and without an entry the answer is "never", so a refusal
      // notice about an override stays on screen after the rater has replaced
      // the very write it is complaining about.
      sentAnswers.current.set(key, {
        answer: { key, value: now.value, na: false, note: "" },
        at: Date.now(),
      });
      push({
        key,
        participantId: seat,
        label: "The night overall",
        target: { kind: "overall", value: now.value },
      });
    });
  }

  /**
   * A boundary, and what the answer in front of the rater does about it.
   *
   * The decision itself is `boundaryWrite`, deliberately kept pure and out of
   * here. This is only the wiring: read the newest draft, ask, and either send it
   * or leave the settle window alone. Leaving it alone matters — at a `"settle"`
   * the whole point can be that a write is *about* to happen from the gem the
   * rater is reaching for, and dropping the window here is what used to send half
   * an answer ahead of it.
   */
  function commit(boundary: Boundary): void {
    // The overall's own window, forced out at every boundary the step's is. It
    // has no held-replacement question and no half-answer state — a number is
    // either in it or it is not — so there is nothing to decide, only to flush.
    overallSettler.flush();
    const answer = draftRef.current;
    // A replacement still waiting on its question. At a `"settle"` it simply
    // waits — the rater is still here and may yet answer. At a `"leave"` the
    // question has been answered by leaving: the stored rating stands and the
    // replacement is dropped, draft and settle window together, so it cannot
    // arrive at the table behind a phone that has moved to the next seat.
    if (heldFor(answer)) {
      if (boundary === "leave") {
        settler.drop(cellKey);
        forgetDraft();
      }
      return;
    }
    const write =
      actorId !== null &&
      boundaryWrite({
        draft: answer,
        cellKey,
        // What the table holds *as far as this screen may know it* — null on a
        // shared device, where the dedupe is off rather than the cell empty. See
        // `ownAnswer`.
        committed: own.shown,
        sent: sentAnswers.current.get(cellKey)?.answer ?? null,
        boundary,
      });
    if (write && answer !== null && actorId !== null) {
      // `send` drops this cell's window itself: whatever is waiting in it is
      // this same answer or an older draft of it, and the write going now is
      // built from the newest.
      send(answer, step, actorId);
      return;
    }
    // Nothing to write, and the rater is going. A value still inside the window
    // would land after the screen had moved on, so it is dropped rather than
    // flushed — it is either what the table already holds or a draft of the
    // answer that has just been decided against. Scoped to this cell: a value
    // waiting for a step somebody has already left keeps its own window.
    if (boundary === "leave") settler.drop(cellKey);
  }

  commitBoundary.current = commit;

  /**
   * "Yes, replace it."
   *
   * The write goes from here rather than from the next `commit`, because there
   * may not be one: the rater who taps this has finished with the cell and the
   * next thing they touch is Next, which on a shared device hands the phone on.
   * `send` is asked for directly for a whole answer only — a note with no number
   * is still half an answer and still waits for a `"leave"`, which is the rule
   * `boundaryWrite` holds everywhere else and it does not change here.
   */
  function confirmReplace(): void {
    setReplaceOk(cellKey);
    const answer = draftRef.current;
    if (answer === null || answer.key !== cellKey || actorId === null) return;
    // Whole is asked of the row that would go, not of the draft: words typed
    // over a hidden number are a complete answer once the number is carried
    // forward, and holding those back for a `"leave"` would be waiting for a
    // half they already have.
    if (!wholeAnswer(replacementRow(answer, stored))) return;
    send(answer, step, actorId);
  }

  /** "No, keep mine." The draft goes; what is on the table was never touched. */
  function keepStored(): void {
    settler.drop(cellKey);
    forgetDraft();
  }

  function pick(participant: SessionParticipant): void {
    // The chip is disabled as well; the rule is restated where the seat is
    // actually taken so no later caller can route around the markup.
    if (seatTakenElsewhere({ phrasing, role, grantSeatId, ownSeatId, seat: participant })) return;
    setActorId(participant.id);
    forgetDraft();
    setSaving(null);
    if (mode === "MULTI_DEVICE") {
      saveSeat(sessionId, participant.id);
      setSeatMemory(participant.id);
    }
  }

  /**
   * The host taking a name back off the phone that took it.
   *
   * Straight to the server, never through the outbox: the answers this discards
   * are a guest's, they are not in this device's IndexedDB, and a queued
   * revocation that landed an hour later would take a seat somebody had by then
   * legitimately been given. It is also the one write here that must not be
   * retried blind — the state is re-read on success, and a failure says so and
   * leaves the seat exactly where it was.
   */
  function takeBack(participant: SessionParticipant): void {
    if (takingBack !== null) return;
    // Reachable now from the middle of the loop, where the host may be holding a
    // rating that has not left the settle window — and this call ends in a
    // `refresh()`. Nothing about that unmounts the screen, so the window would
    // in fact still close on its own; the commit is here so that the rule ("a
    // path that reaches the server forces the value out first") does not have to
    // be re-derived by the next person to move this button. A no-op on the seat
    // picker, where there is no actor and therefore no draft.
    commit("leave");
    setTakingBack(participant.id);
    setTakeBackError(null);
    void takeBackSeat(sessionId, participant.id)
      .then(() => refresh())
      .catch((err: unknown) => {
        setTakeBackError(
          err instanceof ApiError && err.serverSaid
            ? err.message
            : "That name is still on the other phone. Check the connection and try again.",
        );
      })
      .finally(() => setTakingBack(null));
  }

  /**
   * A skip is an answer, and an answer has to be a row.
   *
   * The reveal gate counts committed rows, so a skip that wrote nothing left the
   * aspect blind for the whole table and there was no way back to it. The row
   * goes in as n/a — the only shape without a number the database accepts.
   * The headline is exempt: it is the one number nobody skips.
   *
   * Both ways of leaving a step write it: the shared device's hand-off below, and
   * the plain "Next" of a QR night. `skipRowNeeded` is the single guard, so the
   * two modes cannot drift apart again.
   *
   * A draft in hand is never a skip. `skipRowNeeded` is asked about `answered`,
   * which is built from this render's state, while every other write path now
   * reads the newest draft — so a note typed in the same dispatch as the tap on
   * Next would be seen here as nothing at all, and this would write an empty n/a
   * over it and then `commit("leave")` would write the note as a second row for
   * the same cell. The draft belongs to the boundary; this is only for a rater
   * who has given no answer whatsoever — which is `drafted`, the same predicate
   * the replacement gate asks, so the two cannot come to disagree about what
   * counts as having said something. A field holding nothing but spaces is one
   * of those disagreements: it used to read as an answer here and as silence
   * everywhere else, and the aspect stayed blind for the whole table.
   */
  function skipWrite(): void {
    if (drafted(draftRef.current, cellKey)) return;
    if (skipRowNeeded(step.kind, actorId, answered) && actorId) {
      send({ key: cellKey, value: null, na: true, note: "" }, step, actorId);
    }
  }

  function skipOn(): void {
    skipWrite();
    passOn();
  }

  function passOn(): void {
    // `commit` first, and it reads the draft: the answer leaves for the table
    // before the screen forgets it, which is the whole of rule 2 at the top of
    // this file.
    commit("leave");
    setLastActor(actorId);
    setActorId(null);
    forgetDraft();
    setSaving(null);
  }

  /**
   * Open a topic's follow-ups and step into the first of them.
   *
   * Deliberately no `skipWrite`. Every other way off a topic writes an n/a for a
   * rater who put nothing on it, because leaving silently keeps the whole table
   * blind — but this way off is not leaving, it is answering the topic the long
   * way, and an n/a written here would be a "doesn't apply" on the one topic
   * somebody cared enough about to open. The reveal gate counts the follow-up
   * rows instead; see `settledOnTopic` on the server.
   *
   * A draft in hand is still committed, exactly as Next commits it. Discarding a
   * rating somebody has already tapped would be worse than the confusion of
   * having set one — and it is not silent: the topic step says "set by you" over
   * the figure from then on, next to the follow-ups it now overrides.
   */
  function openDetail(aspectId: string): void {
    commit("leave");
    setDetailed((prev) => (prev.has(aspectId) ? prev : new Set(prev).add(aspectId)));
    setStepIndex(index + 1);
    forgetDraft();
    setSaving(null);
  }

  function go(to: number): void {
    commit("leave");
    setStepIndex(to);
    forgetDraft();
    setSaving(null);
    // A new category starts the round again: the phone goes back to whoever is
    // first, rather than staying with whoever happened to finish the last one.
    if (mode === "SHARED_DEVICE" && role === "owner") {
      setActorId(null);
      setLastActor(null);
    }
  }

  // `channelNotice` promises the offline write is held on the device, which is
  // only true where the outbox runs. A guest phone is just cut off.
  const transport = channel === "offline" ? offlineLine(writePath) : channelNotice(channel);

  const savingHere = saving && saving.key === cellKey ? saving : null;
  const unsentHere = unsentList.some((o) => o.key === cellKey);
  const refusedHere = refusedList.some((o) => o.key === cellKey);
  const needsSeat = actorId === null;
  const strandedGuest = needsSeat && role === "guest";
  const suggested = needsSeat ? nextRater(step, participants, followUpsHere, lastActor) : null;
  const canFinish = state.headline.revealed;

  // The local draft counts here as well as the server's row: the phone should not
  // call it a skip a beat before the echo of the write comes back. `own.committed`
  // and not a value, because on a shared device this screen may not know what the
  // seat said — only that it said something, which is precisely what a skip must
  // never write an empty n/a over.
  //
  // `detailCommitted` is the new half. A rater who went into detail has answered
  // this topic — six times over — and has no `aspect_rating` row to show for it,
  // so without this Next would write an empty n/a on the way past and the topic
  // they answered most carefully would be the one recorded as skipped.
  const answered =
    own.committed ||
    detailCommitted > 0 ||
    live.na ||
    live.value !== null ||
    live.note.trim() !== "";

  const forward = forwardAction({
    phrasing,
    needsSeat,
    waiting: progress.waiting.map((p) => p.name_snapshot),
    isLast,
    canFinish,
    answered,
  });

  function runForward(): void {
    if (!forward) return;
    switch (forward.kind) {
      case "pass":
        skipOn();
        return;
      case "results":
        commit("leave");
        navigate(`/session/${sessionId}/results`);
        return;
      case "next":
        // On a QR night this is the only way past an aspect, so it carries the
        // same skip write the shared device's hand-off does. Advancing without
        // one leaves the table waiting on somebody who has moved on.
        skipWrite();
        go(index + 1);
        return;
      case "leave":
        /*
         * Deliberately no rows for the people being left behind.
         *
         * `skipWrite` above puts a real n/a row in, and has to: the reveal gate
         * counts committed rows, so a rater who leaves a step without one keeps
         * the whole table blind on it. The obvious reading is that this button
         * should do the same for everyone still waiting, and clear the step out
         * of the way properly. It must not, for two reasons.
         *
         * The first is the app's. An answer belongs to the person who gave it.
         * An n/a invented on somebody's behalf is not a skip, it is a fabricated
         * answer that then counts towards a reveal the rest of the table reads
         * as complete — and the person it was written for never knows it was
         * said in their name.
         *
         * The second is the server's, and it is not up for discussion: a write
         * to a seat a live join grant holds is refused outright
         * (`heldByGuestDevice`). So on exactly the night where people are
         * missing — everyone on their own phone — some of those rows would land
         * and the rest would come back refused, one line of "Not saved" per
         * person, leaving a step half-answered by a host who never touched it.
         *
         * So the cost is paid out loud instead of quietly written away: the
         * button names who is being left, the hint next to the seat picker says
         * this step stays blind, and Back returns to it for as long as the night
         * is open. `go` commits, so a rating inside the settle window still
         * leaves with the screen.
         */
        go(index + 1);
        return;
    }
  }

  const host = hostControls({
    role,
    revealed: progress.revealed,
    participants,
    committed: progress.committed,
  });

  return (
    <div className="pb-screen">
      {/* What is being rated, kept deliberately quiet. The heading below is the
          aspect, because that is the question in front of the person holding the
          phone; the game is context they need available, not announced. It
          matters most on a passed-around phone, where whoever has just been
          handed it did not see the setup screen. */}
      <div className="pb-gamestrip">
        <BoxArt
          game={{
            title: state.game_title,
            thumb_url: state.game_thumb_url,
            image_url: state.game_image_url,
          }}
          size={32}
        />
        <span className="pb-gamestrip__title">{state.game_title}</span>
      </div>

      <div className="pb-screen__head">
        <h1 className="pb-screen__title">{step.title}</h1>
        <span className="pb-screen__sub">
          {/* A follow-up read as a stray question without the axis it hangs off,
              so the aspect is named next to it rather than three steps back. */}
          {step.kind === "subquestion" ? `Under ${step.parentTitle} · ` : ""}
          Step {index + 1} of {steps.length}
        </span>
      </div>

      {/* Progress, not a control: a 14px hole is under the tap target this app
          holds itself to, so the steps are walked with the buttons below. */}
      <div className="pb-track" aria-hidden="true">
        {steps.map((s, i) => (
          <Fragment key={s.key}>
            {i > 0 ? <span className="pb-track__rule" /> : null}
            <span
              className="pb-track__hole"
              data-state={i === index ? "current" : s.view.revealed ? "done" : undefined}
            />
          </Fragment>
        ))}
      </div>

      {transport ? (
        <Notice tone={channel === "offline" ? "alert" : "wait"}>{transport}</Notice>
      ) : null}
      {error ? <Notice tone="alert">{error}</Notice> : null}

      {unsentList.length > 0 ? (
        <div className="pb-notice" data-tone="alert">
          <span className="pb-notice__strong">Not sent</span>
          <span>
            {unsentList.length === 1
              ? `One rating (${unsentList[0]?.label ?? "a rating"}) has not reached the table yet.`
              : `${unsentList.length} ratings have not reached the table yet.`}
            {/*
              The reason was collected all along and thrown away at the edge of
              the screen. "Check your signal" and "this session is closed" call
              for opposite responses, and only the server knows which it is.
              `unsentDetail` drops the half of it that repeats the line above.
            */}
            {unsentList[0]?.message ? ` ${unsentList[0].message}` : null} Retrying on its own.
          </span>
          <button
            type="button"
            className="pb-btn"
            data-variant="quiet"
            onClick={() => {
              for (const out of [...unsent.current.values()]) push(out);
            }}
          >
            Try again
          </button>
        </div>
      ) : null}

      {/*
        A refusal, said as a refusal. No "Retrying on its own" and no Try again
        button: the server has decided, the same request would be turned down in
        the same words, and offering the tap would be inviting somebody to find
        that out five times. The sentence is the server's because the two
        refusals a table meets — the answer is settled, or the name is on
        somebody else's phone — want opposite things from whoever is reading.

        Which questions, by name. A bare count is not something anybody at a
        table can act on, and the only thing tying it to an answer was a cell key
        made of two ULIDs.
      */}
      {refusedList.length > 0 ? (
        <div className="pb-notice" data-tone="alert">
          <span className="pb-notice__strong">Not saved</span>
          <span>{refusedLine(refusedList)}</span>
        </div>
      ) : null}

      {strandedGuest ? (
        <div className="pb-section">
          <Notice tone="alert" strong="No seat">
            This phone&rsquo;s seat is not in this session any more. Scan the code again to rejoin.
          </Notice>
        </div>
      ) : needsSeat ? (
        <>
          <HandOff
            phrasing={phrasing}
            step={step}
            participants={participants}
            role={role}
            grantSeatId={grantSeatId}
            ownSeatId={ownSeatId}
            suggested={suggested}
            onPick={pick}
            takingBack={takingBack}
            takeBackError={takeBackError}
            onTakeBack={role === "owner" ? takeBack : undefined}
          />
          {/*
            The whole price of the quiet button in the footer, said where there
            is room to say it. A button label can name who gets left behind; it
            cannot explain that the gems never land on a step the table did not
            finish, which is the part that cost QA two aspects without their
            noticing. Rendered exactly when that button is, so the two cannot
            drift apart.
          */}
          {forward?.kind === "leave" ? (
            <p className="pb-hint">
              Nobody has to answer this one. Move on and{" "}
              {nameList(progress.waiting.map((p) => p.name_snapshot))} never put anything on{" "}
              {step.title} — the gems only land once the whole table has answered, so this one
              stays hidden until they do. Back brings you here again any time tonight.
            </p>
          ) : null}
        </>
      ) : (
        <>
          <div className="pb-section">
            {/* The way out of a seat, on either kind of night — see
                `hostControls` for why it is the host's and not a guest's.
                `passOn` commits first, so changing seat cannot leave a rating
                behind in the settle window. */}
            {host.seatChange ? (
              <div className="pb-chips">
                <button type="button" className="pb-chip" data-on="true" onClick={passOn}>
                  <Swatch colour={actor?.colour_snapshot ?? "transparent"} />
                  Rating as {actor?.name_snapshot ?? "someone"}
                  <span className="pb-chip__meta">change</span>
                </button>
              </div>
            ) : null}

            <Stars
              // The worked-out figure while it is the answer, and this rater's
              // own the moment they have picked the control up. Arming drops
              // back to the draft — which is null — on purpose: an armed control
              // still showing the derived number would refuse the tap that lands
              // on it (`planCommit` calls a restatement no change), so the one
              // gesture that means "yes, that one" would be the only one that
              // wrote nothing. The figure does not disappear; it moves to the
              // line underneath, where it reads as what it is.
              value={derivedShown && !armedHere ? derivedShown.value : live.na ? null : live.value}
              locked={lockedStars}
              onArm={() => setArmed(cellKey)}
              onChange={(value) => apply({ value, na: false })}
              aspectName={step.title}
              playerName={actor?.name_snapshot}
              blind
              isOwn
              revealed={progress.revealed}
              // That this seat has answered is not the secret; what it answered
              // is. Without this the control reads "Not rated" to somebody who
              // rated, which is not a blank slate but a wrong answer — and it is
              // the sentence that made overwriting one look like filling one in.
              // `detailCommitted` only where the figure is unreadable: there the
              // topic has an answer — six of them — and no `aspect_rating` row
              // to prove it, so the readout would say "Not rated" to the one
              // person who answered it hardest. Where it *is* readable the
              // number itself is on the stars, and claiming "hidden until
              // reveal" over a number that is right there would be the lie
              // pointing the other way.
              committed={own.committed || (!readable && detailCommitted > 0)}
              // Deliberately still live after the reveal. The reveal used to be
              // the deadline — everyone else's answer is on screen from here, so
              // an input left open lets whoever rated last revise theirs knowing
              // what they are moving against. That is a real cost and it was
              // weighed: a rating you cannot correct is worse, and on a table of
              // one the deadline landed on the rater's own first tap. The blind
              // half of the ritual is untouched; only the lock is gone.
              naEnabled={allowsNa(step)}
              na={live.na}
              // Only the "yes, this doesn't apply" direction is an answer worth
              // writing. Clearing the box leaves an unset rating, and an unset
              // rating is not a row.
              onNaChange={(na) => apply({ na, value: na ? null : live.value })}
            />

            {/* The night's overall, beside the headline and not instead of it.
                They are two different questions and the difference is the whole
                reason both are here: the headline is "do you want to play this
                again", this is "how good was the night". A group can badly want
                a rematch at a game they rated middling.

                Default is the mean of this seat's topics, and it is never
                stored that way — a row on `overall_rating` exists only where
                somebody armed these stars and moved them. Skipped topics are not
                in the mean at all: they are not zeroes, they are silence. */}
            {step.kind === "headline" && actorId ? (
              <>
                <Stars
                  value={overallShown}
                  locked={overallLocked}
                  onArm={() => setOverallArmed(overallKey)}
                  onChange={applyOverall}
                  aspectName="The night overall"
                  playerName={actor?.name_snapshot}
                  blind
                  isOwn
                  revealed={progress.revealed}
                  committed={overallDerived?.source === "set"}
                />
                <p className="pb-hint">
                  {overallArmedHere && overallLive === null ? (
                    <>Tap a star to put your own number on the night.</>
                  ) : overallDerivedShown ? (
                    <>
                      {formatStars(overallDerivedShown.value)}
                      {/* The rubric word, named the way the topic control names
                          its own: an approximation to the nearest rung, because
                          a mean of six topics lands between them far more often
                          than on one. `OVERALL_STOPS` is otherwise a ladder only
                          the results screen ever reads out. */}
                      {starLabel(OVERALL_STOPS, overallDerivedShown.value)
                        ? ` · about “${starLabel(OVERALL_STOPS, overallDerivedShown.value)}”`
                        : null}{" "}
                      — worked out from {overallDerivedShown.topics} of {state.aspects.length} topics
                      above. Tap the stars to set it yourself instead.
                    </>
                  ) : overallLive !== null || overallDerived?.source === "set" ? (
                    <>Set by you, over whatever the topics above work out to.</>
                  ) : readable ? (
                    <>
                      Nothing rated above yet, so there is nothing to work this out from. Tap the
                      stars to set it yourself.
                    </>
                  ) : (
                    // On a shared device nothing is read back, not even to the
                    // seat rating right now — so the sentence above would be a
                    // claim about topics this screen deliberately cannot see,
                    // and it read "nothing rated above yet" under a night that
                    // had been rated six times.
                    <>Worked out from the topics above unless you tap the stars and set it yourself.</>
                  )}
                </p>
              </>
            ) : null}

            {/* One line, and it says where the number came from — a worked-out
                figure that looks like a typed one is the whole failure mode
                here. The rubric word is an approximation and is marked as one:
                the mean lands between stops far more often than on one, so
                `starLabel` is naming the nearest, not translating. */}
            {derived && derived.source === "derived" && derived.value !== null ? (
              <p className="pb-hint">
                {armedHere ? (
                  <>Tap a star to put your own number on {step.title}.</>
                ) : (
                  <>
                    {formatStars(derived.value)}
                    {starLabel(stops, derived.value)
                      ? ` · about “${starLabel(stops, derived.value)}”`
                      : null}{" "}
                    {/* The total is the questions the night asks, not the rows
                        this device can read: a follow-up still redacted is one
                        of the six either way, and "3 of 3" under a night that
                        asked six would be a lie told by the redaction. */}— worked out from{" "}
                    {derived.answered} of {followUpsHere.length} answers below. Tap the stars to set
                    it yourself instead.
                  </>
                )}
              </p>
            ) : null}

            {/* The same line on the night where the figure may not be shown.
                `derived` is null there by design — a mean of six answers is
                one subtraction from the answers — but *that* the seat answered
                is not the secret, and saying nothing leaves a rater who filled
                in six questions looking at a topic that claims it was skipped.
                So: the provenance without the number, which is exactly what the
                gate allows. Counted from `detailCommitted` rather than from
                `derived`, because that count never touches a value. */}
            {step.kind === "aspect" &&
            !readable &&
            detailCommitted > 0 &&
            live.value === null &&
            !live.na ? (
              <p className="pb-hint">
                Worked out from {detailCommitted} of {followUpsHere.length} answers below, and
                hidden until everyone is in — this phone is going round the table. Tap the stars to
                set it yourself instead.
              </p>
            ) : null}

            {/* The branch, per topic. Six topics with six follow-ups each is
                forty-two screens if the follow-ups are the spine; they are not
                — the topic is, and this opens the ones somebody actually cares
                about. It only ever opens: `openDetail` adds to a set that is
                never shrunk, because taking a block back out would shorten the
                step list under an index somebody else is standing on. */}
            {step.kind === "aspect" && followUpsHere.length > 0 && !detailed.has(step.key) ? (
              <div className="pb-chips">
                <button
                  type="button"
                  className="pb-chip"
                  data-on="false"
                  onClick={() => openDetail(step.key)}
                >
                  Go into detail
                  <span className="pb-chip__meta">{followUpsHere.length} questions</span>
                </button>
              </div>
            ) : null}

            {/* One note per category, at the end of it — `NoteBlock` carries the
                reasoning and which step it lands on. A block that is a lone
                aspect, which is every block in the seeded taxonomy, puts it
                exactly where it has always been. */}
            {step.kind !== "headline" && step.noteBlock ? (
              <label className="pb-field">
                <span className="pb-label">It&rsquo;s complicated?</span>
                {step.kind === "subquestion" ? (
                  <span className="pb-hint">
                    One note for {step.noteBlock.aspectTitle}, not for this question on its own.
                  </span>
                ) : null}
                {/* A note on its own is a real answer — it commits a row, so the
                    table is not left waiting on someone who said their piece in
                    words instead of a number. It commits it when the rater has
                    finished making it, though, and not on losing focus: the
                    browser blurs this field on the way to every rating stop, so
                    a write here was a value-less row racing the number that was
                    being tapped, and a value-less row can be the one that fires
                    the reveal. `commit` and `boundaryWrite` hold that rule. */}
                {/* Left live after the reveal for the same reason the stars are:
                    a note is part of the answer, so locking one and not the
                    other would only move the edit rather than prevent it. */}
                <textarea
                  className="pb-textarea"
                  value={live.note}
                  // The same hole the stars had: an empty field under "say it in
                  // words" reads as nothing written, and typing into it used to
                  // replace a sentence nobody could see. The field stays empty —
                  // the words are as blind as the number — but it says so.
                  placeholder={
                    sealed
                      ? "Answered — hidden until reveal. Type to replace it."
                      : "Say it in words instead of a number."
                  }
                  onChange={(e) => apply({ note: e.currentTarget.value })}
                  onBlur={() => commit("settle")}
                />
              </label>
            ) : step.kind === "headline" ? (
              <p className="pb-hint">
                The one number nobody skips. Everything else tonight was optional.
              </p>
            ) : null}

            {/* The question a sealed cell has to ask before it writes. Built
                here rather than reached for from `ConfirmButton`, whose shape is
                a button that opens its own banner: the trigger here is the tap
                on a star that has already happened, so the banner arrives with
                the change and the buttons answer for it. */}
            {held ? (
              <>
                <Notice tone="alert" strong="Replace your answer?">
                  {actor?.name_snapshot ?? "This seat"} already rated {step.title} tonight, and that
                  answer stays hidden until the reveal — so this cannot show you what it was.
                  Replacing writes over what you have changed here and leaves the rest of it
                  standing. Nothing goes to the table until you say.
                </Notice>
                <div className="pb-actions">
                  <button
                    type="button"
                    className="pb-btn"
                    data-variant="primary"
                    onClick={confirmReplace}
                  >
                    Replace it
                  </button>
                  <button type="button" className="pb-btn" onClick={keepStored}>
                    Keep the saved one
                  </button>
                </div>
              </>
            ) : null}

            <div className="pb-chips">
              {refusedHere ? (
                <span className="pb-badge" data-tone="error">
                  Not saved
                </span>
              ) : unsentHere ? (
                <span className="pb-badge" data-tone="error">
                  Not sent
                </span>
              ) : savingHere ? (
                <span
                  className="pb-badge"
                  data-tone={savingHere.status === "saving" ? "pending" : "ok"}
                >
                  {savingHere.status === "saving"
                    ? "Saving"
                    : outbox
                      ? "Saved on this phone"
                      : "Saved"}
                </span>
              ) : null}
            </div>
          </div>

          <div className="pb-section">
            {progress.revealed ? (
              <Notice tone="ok" strong="Everyone in">
                Here is the table. Answers are settled — nobody moves theirs now that they can see
                everyone else&rsquo;s.
              </Notice>
            ) : progress.everyoneIn && stuckHere ? (
              // Same screen state, opposite cause: the answers are here and it is
              // this device that has not delivered them. Say so, and say it is
              // being retried, rather than pointing at the rest of the table.
              <Notice tone="alert" strong="Still here">
                Everyone here has answered, but this device could not reach the server, so the
                gems cannot land yet. It keeps trying on its own — no need to start over.
              </Notice>
            ) : progress.everyoneIn ? (
              // Not a reveal. Every answer this device knows about is in, and the
              // reveal is still the server's to give — which is exactly what an
              // unsynced shared device looks like.
              <Notice tone="wait" strong="Blind">
                Everyone here has answered. Waiting for the table to catch up before the gems land.
              </Notice>
            ) : (
              <Notice tone="wait" strong="Blind">
                {waitLine(phrasing, progress, isLast, canFinish, state)}
              </Notice>
            )}

            {progress.revealed ? (
              <Revealed step={step} cells={cells} ratings={topicRatings} />
            ) : (
              <>
                <Waiting cells={cells} />
                <div className="pb-blind-veil">Everyone&rsquo;s gems land together</div>
              </>
            )}
          </div>

          {/*
            The stuck table's only way out, put where a stuck table actually is
            — see `hostControls`. One quiet tap in front of it, because this is
            the only control in the app that throws an answer away and the state
            that calls for it is indistinguishable, from here, from a table that
            is simply still thinking.
          */}
          {host.takeBack.length > 0 ? (
            seatHelp ? (
              <TakeBack
                seats={host.takeBack}
                prompt={{
                  label: "Waiting on a phone that isn't coming back?",
                  hint:
                    "Take that name back off the phone holding it. Anything on it nobody has read yet is thrown away — not even you see it — and answers this table has already read stay on the board. The name comes free, so somebody here can pick it up with change and finish this one.",
                }}
                busy={takingBack}
                error={takeBackError}
                onTakeBack={takeBack}
              />
            ) : (
              <div className="pb-chips">
                <button
                  type="button"
                  className="pb-chip"
                  data-on="false"
                  onClick={() => setSeatHelp(true)}
                >
                  Somebody&rsquo;s phone gone?
                </button>
              </div>
            )
          ) : null}
        </>
      )}

      <div className="pb-actions">
        <button
          type="button"
          className="pb-btn"
          data-variant="quiet"
          disabled={index === 0}
          onClick={() => go(index - 1)}
        >
          Back
        </button>
        {/* Absent on a screen with no honest way forward — the seat picker on a
            phone of one's own, where the only thing to do is sit down. Back is
            the whole footer there, and going back has never been destructive. */}
        {forward ? (
          <button
            type="button"
            className="pb-btn"
            data-variant={forward.variant}
            disabled={forward.disabled}
            onClick={runForward}
          >
            {forward.label}
          </button>
        ) : null}
      </div>

      {/*
        Last on the screen, and only on the host's own device. A guest phone is
        holding one seat at somebody else's night; the server would refuse it
        anyway, and offering a control that answers 403 is worse than not
        offering it. Below the footer because it is the way out of the night
        rather than a way through it — nothing above it should have to be
        scrolled past to reach Back.
      */}
      {role === "owner" ? (
        <DeleteNight
          sessionId={sessionId}
          onDeleted={() => navigate("/", { replace: true })}
        />
      ) : null}
    </div>
  );
}

/**
 * One sentence about who the table is waiting on — and, on the last step, why it
 * matters, since the headline is the only thing that gates the results.
 */
function waitLine(
  phrasing: CaptureMode,
  // The caller's, not a second one worked out here. It used to recompute the
  // progress from the step alone, which is the call that cannot see a seat's
  // follow-ups — so the roster and this sentence would have disagreed about who
  // the table is waiting on, with the sentence being the wrong one.
  progress: Progress,
  isLast: boolean,
  canFinish: boolean,
  state: Parameters<typeof missingHeadline>[0],
): string {
  const line = waitingLabel(phrasing, progress.waiting);
  if (isLast && !canFinish) {
    const missing = waitingLabel(phrasing, missingHeadline(state));
    return `${missing ?? line ?? "One number is still missing"} — the results open once everyone has put a number on it.`;
  }
  return line ?? "Nobody has answered this one yet.";
}
